<template>
  <div class="fh-page">
    <div
      v-if="loadingCourses"
      class="fh-state"
    >
      <div class="fh-spinner" />
      <p>Chargement du parcours…</p>
    </div>

    <div
      v-else-if="coursesError"
      class="fh-state"
    >
      <strong>Parcours indisponible</strong>
      <p>{{ coursesError }}</p>
      <button
        type="button"
        class="fh-btn ghost"
        @click="bootstrapJourney"
      >
        Réessayer
      </button>
    </div>

    <div
      v-else-if="courses.length === 0"
      class="fh-state"
    >
      <strong>Aucun cours</strong>
      <p>Crée ou rejoins un cours pour voir ton parcours.</p>
      <button
        type="button"
        class="fh-btn primary"
        @click="$router.push({ name: 'dashboard-student-personal-courses' })"
      >
        Ouvrir mes cours
      </button>
    </div>

    <div
      v-else
      class="fh-shell"
    >
      <aside class="fh-sidebar">
        <div class="fh-topbar">
          <button
            type="button"
            class="fh-back"
            @click="goBack"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span>Retour</span>
          </button>
          <div class="fh-brand">
            TUT'TOP · Parcours
          </div>
        </div>

        <div
          v-if="courses.length > 1"
          class="fh-course-select"
        >
          <label>Projet</label>
          <select
            :value="selectedCourseId"
            @change="onCourseChange($event.target.value)"
          >
            <option
              v-for="course in courses"
              :key="getEntityId(course)"
              :value="getEntityId(course)"
            >
              {{ humanizeLabel(course.title) }}
            </option>
          </select>
        </div>

        <div
          v-if="selectedCourseId && !embeddedPreview"
          class="fh-course-remove"
        >
          <button
            type="button"
            class="fh-course-remove-btn"
            :disabled="removingCourse"
            @click="showRemoveCourseModal = true"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
            <span>{{ removingCourse ? 'Suppression…' : 'Retirer ce cours' }}</span>
          </button>
        </div>

        <!-- Modal confirmation retrait cours -->
        <b-modal
          v-model="showRemoveCourseModal"
          centered
          hide-header
          hide-footer
          no-enforce-focus
        >
          <div class="fh-remove-modal">
            <div class="fh-remove-modal-icon">
              🗑️
            </div>
            <h4>Retirer ce cours ?</h4>
            <p>
              <strong>{{ activeCourse && humanizeLabel(activeCourse.title) }}</strong> sera retiré de ton Parcours.
              <br>
              <span v-if="activeCourse && !activeCourse.tutor">Le cours personnel et tout son contenu seront supprimés.</span>
              <span v-else>Tu te désinscris du cours tutoré.</span>
            </p>
            <div class="fh-remove-modal-actions">
              <button
                class="fh-btn ghost compact"
                @click="showRemoveCourseModal = false"
              >
                Annuler
              </button>
              <button
                class="fh-btn danger compact"
                :disabled="removingCourse"
                @click="removeCourseFromJourney"
              >
                {{ removingCourse ? 'Suppression…' : 'Confirmer' }}
              </button>
            </div>
          </div>
        </b-modal>

        <div
          v-if="journey"
          class="fh-journey-head"
        >
          <span class="fh-kicker">Parcours</span>
          <h2>{{ displayCourseTitle }}</h2>
          <p>{{ displayJourneySummary }}</p>
        </div>

        <div
          v-if="showLessonMode"
          class="fh-plan-side"
        >
          <div class="fh-progress">
            <div class="fh-progress-bar">
              <div
                class="fh-progress-fill"
                :style="{ width: `${lessonPlanProgress.percent}%` }"
              />
            </div>
            <span class="fh-progress-text">
              {{ lessonPlanProgress.completed }} / {{ lessonPlanProgress.total }} leçons · {{ lessonPlanProgress.percent }}%
            </span>
          </div>

          <div
            v-if="lessonPlanNeedsUpgrade"
            class="fh-upgrade-card"
          >
            <strong>Version enrichie disponible</strong>
            <p>Régénère ce parcours pour obtenir des objectifs plus clairs, des formules mieux mises en avant et un ton plus premium.</p>
            <button
              type="button"
              class="fh-btn ghost compact"
              :disabled="generatingPlan"
              @click="generateLessonPlan"
            >
              {{ generatingPlan ? 'Mise à jour…' : 'Mettre à jour ce cours' }}
            </button>
          </div>

          <span class="fh-kicker">Le cours</span>
          <ul class="fh-lessons">
            <li
              v-for="lesson in lessons"
              :key="lesson.lessonId"
              class="fh-lesson-item"
              :class="[`fh-lesson-${lesson.status}`, { active: currentLesson && currentLesson.lessonId === lesson.lessonId, adaptive: isAdaptiveLesson(lesson) }]"
              @click="selectLesson(lesson)"
            >
              <span class="fh-lesson-num">{{ lessonBadgeLabel(lesson) }}</span>
              <div class="fh-lesson-info">
                <span
                  v-if="isAdaptiveLesson(lesson)"
                  class="fh-lesson-kind"
                >
                  {{ adaptiveKindLabel(lesson) }}
                </span>
                <span
                  class="fh-lesson-title"
                  v-html="formatInline(lessonDisplayTitle(lesson))"
                />
                <span
                  v-if="lesson.subtitle"
                  class="fh-lesson-sub"
                  v-html="formatInline(lesson.subtitle)"
                />
              </div>
              <span class="fh-lesson-dot">{{ lessonIcon(lesson) }}</span>
            </li>
          </ul>

        </div>
      </aside>

      <main class="fh-main">
        <div
          v-if="loadingJourney"
          class="fh-main-state"
        >
          <div class="fh-spinner small" />
          <span>Chargement…</span>
        </div>

        <div
          v-else-if="lessonPlanGenerating"
          class="fh-main-state"
        >
          <div class="fh-spinner small" />
          <strong>Nick prépare ton cours…</strong>
          <span>Cela prend ~30 secondes. Il compose l'intro, les leçons et les questions à partir de ton contenu.</span>
        </div>

        <div
          v-else-if="showLessonMode && currentLesson"
          class="fh-lesson-view"
        >
          <LearningProfileCard
            v-if="learnerProfile"
            :profile="learnerProfile"
            class="fh-learner-card"
            @edit="openProfileModal"
            @onboard="openOnboarding"
          />

          <PathAdaptationCard
            v-if="latestPathEvent"
            :event="latestPathEvent"
            :history="recentPathEvents"
            class="fh-path-card"
          />

          <div
            v-if="lessonPlan.intro && currentLesson.order === 0 && !isAdaptiveLesson(currentLesson)"
            class="fh-intro-card"
          >
            <span class="fh-intro-tag">Intro</span>
            <div
              class="fh-rich-body"
              v-html="formatRich(lessonPlan.intro)"
            />
          </div>

          <header class="fh-lesson-head">
            <span class="fh-lesson-kicker">
              {{ lessonKicker(currentLesson) }}
            </span>
            <h1 v-html="formatInline(lessonDisplayTitle(currentLesson))" />
            <p
              v-if="currentLesson.subtitle"
              v-html="formatInline(currentLesson.subtitle)"
            />
          </header>

          <section
            v-if="isAdaptiveLesson(currentLesson)"
            class="fh-lesson-block adaptive"
            :class="`kind-${currentLesson.adaptiveKind || 'remediation'}`"
          >
            <span class="fh-block-kicker">Adaptation de Nick</span>
            <div class="fh-adaptive-meta">
              <span class="fh-adaptive-pill">{{ adaptiveKindLabel(currentLesson) }}</span>
              <span
                v-if="currentLesson.adaptiveConceptLabel"
                class="fh-adaptive-concept"
                v-html="formatInline(currentLesson.adaptiveConceptLabel)"
              />
            </div>
            <p
              v-if="currentLesson.decisionRationale"
              v-html="formatInline(currentLesson.decisionRationale)"
            />
            <div class="fh-adaptive-actions">
              <button
                type="button"
                class="fh-adaptive-explain"
                @click="explainAdaptiveConcept(currentLesson)"
              >
                {{ adaptiveExplainButtonLabel(currentLesson) }}
              </button>
            </div>
          </section>

          <section
            v-if="currentLessonOutcomes.length"
            class="fh-lesson-block outcomes"
          >
            <span class="fh-block-kicker">Dans cette leçon</span>
            <ul class="fh-outcomes-list">
              <li
                v-for="(outcome, outcomeIndex) in currentLessonOutcomes"
                :key="`outcome-${outcomeIndex}`"
                v-html="formatInline(outcome)"
              />
            </ul>
          </section>

          <section
            v-if="currentLesson.narrative"
            class="fh-lesson-block narrative"
          >
            <div
              class="fh-rich-body"
              v-html="formatRich(currentLesson.narrative)"
            />
          </section>

          <section
            v-if="currentLesson.whyItMatters"
            class="fh-lesson-block why"
          >
            <span class="fh-block-kicker">Pourquoi c'est utile</span>
            <div
              class="fh-rich-body"
              v-html="formatRich(currentLesson.whyItMatters)"
            />
          </section>

          <section
            v-if="currentLessonFormula"
            class="fh-lesson-block formula"
          >
            <span class="fh-block-kicker">{{ currentLessonFormula.title || 'Formule clé' }}</span>
            <div
              class="fh-formula-box"
              v-html="renderFormulaBlock(currentLessonFormula.latex)"
            />
            <p
              v-if="currentLessonFormula.explanation"
              class="fh-formula-explanation"
            >
              {{ currentLessonFormula.explanation }}
            </p>
          </section>

          <section
            v-if="currentLesson.keyPoints && currentLesson.keyPoints.length"
            class="fh-lesson-block key-points"
          >
            <span class="fh-block-kicker">À retenir</span>
            <ul>
              <li
                v-for="(point, pIndex) in currentLesson.keyPoints"
                :key="`kp-${pIndex}`"
                v-html="formatInline(point)"
              />
            </ul>
          </section>

          <section
            v-if="currentLesson.example && currentLesson.example.body"
            class="fh-lesson-block example"
          >
            <span
              class="fh-block-kicker"
              v-html="formatInline(currentLesson.example.title || 'Exemple')"
            />
            <div
              class="fh-rich-body"
              v-html="formatRich(currentLesson.example.body)"
            />
          </section>

          <section
            v-if="Array.isArray(currentLesson.practice) && currentLesson.practice.length"
            class="fh-lesson-block practice"
          >
            <span class="fh-block-kicker">Pratique</span>
            <article
              v-for="(question, qIndex) in currentLesson.practice"
              :key="question.questionId"
              class="fh-question"
              :class="{ answered: !!getQuestionAttempt(question.questionId) }"
            >
              <div class="fh-question-head">
                <span class="fh-question-num">{{ qIndex + 1 }}</span>
                <div
                  class="fh-question-prompt"
                  v-html="formatInline(question.prompt)"
                />
              </div>

              <div
                v-if="question.kind !== 'short_answer'"
                class="fh-options"
              >
                <button
                  v-for="(option, optIndex) in question.options"
                  :key="`opt-${question.questionId}-${optIndex}`"
                  type="button"
                  class="fh-option"
                  :class="optionClass(question, optIndex)"
                  :disabled="isQuestionLocked(question.questionId) || !!answeringQuestion[question.questionId] || isOptionAlreadyTriedWrong(question.questionId, optIndex)"
                  @click="submitMcqAnswer(question, optIndex)"
                >
                  <span class="fh-option-letter">{{ String.fromCharCode(65 + optIndex) }}</span>
                  <span
                    class="fh-option-text"
                    v-html="formatInline(option)"
                  />
                </button>
              </div>

              <div
                v-else
                class="fh-short-answer"
              >
                <input
                  :value="answerDrafts[question.questionId] || ''"
                  type="text"
                  placeholder="Écris ta réponse…"
                  :disabled="isQuestionLocked(question.questionId) || !!answeringQuestion[question.questionId]"
                  @input="setAnswerDraft(question.questionId, $event.target.value)"
                  @keyup.enter="submitShortAnswer(question)"
                >
                <button
                  type="button"
                  class="fh-btn primary compact"
                  :disabled="isQuestionLocked(question.questionId) || !!answeringQuestion[question.questionId] || !(answerDrafts[question.questionId] || '').trim()"
                  @click="submitShortAnswer(question)"
                >
                  Valider
                </button>
              </div>

              <div
                v-if="getQuestionFeedback(question.questionId)"
                class="fh-feedback"
                :class="{ correct: getQuestionFeedback(question.questionId).correct, wrong: !getQuestionFeedback(question.questionId).correct }"
              >
                <strong>
                  {{ getQuestionFeedback(question.questionId).correct ? '✓ Bonne réponse' : '✗ Pas tout à fait' }}
                </strong>
                <div
                  v-if="getQuestionFeedback(question.questionId).nudge"
                  class="fh-feedback-nudge"
                  v-html="formatInline(getQuestionFeedback(question.questionId).nudge)"
                />
                <div
                  v-if="getQuestionFeedback(question.questionId).explanation"
                  v-html="formatRich(getQuestionFeedback(question.questionId).explanation)"
                />
                <div
                  v-if="!getQuestionFeedback(question.questionId).correct && getQuestionFeedback(question.questionId).expectedAnswer"
                  class="fh-expected"
                >
                  Réponse attendue :
                  <span v-html="formatInline(getQuestionFeedback(question.questionId).expectedAnswer)" />
                </div>
                <div
                  v-if="!getQuestionFeedback(question.questionId).correct"
                  class="fh-feedback-actions"
                >
                  <button
                    v-if="canRetryQuestion(question)"
                    type="button"
                    class="fh-btn primary compact"
                    @click="retryQuestion(question)"
                  >
                    Je retente
                  </button>
                  <button
                    type="button"
                    class="fh-btn ghost compact"
                    @click="askNickAboutQuestion(question, getQuestionFeedback(question.questionId))"
                  >
                    {{ getQuestionFeedback(question.questionId).conceptLabel
                      ? `Demander à Nick sur « ${getQuestionFeedback(question.questionId).conceptLabel} »`
                      : 'Demander à Nick' }}
                  </button>
                </div>
              </div>
            </article>
          </section>

          <section
            v-if="currentLessonInsight && currentLessonInsight.status === 'ready'"
            class="fh-lesson-block coach-insight"
            :class="`tone-${currentLessonInsight.tone || 'neutral'}`"
          >
            <div class="fh-coach-head">
              <span class="fh-coach-avatar">E</span>
              <div class="fh-coach-head-text">
                <strong>Nick a remarqué</strong>
                <span class="fh-coach-sub">{{ insightToneLabel }}</span>
              </div>
              <button
                type="button"
                class="fh-coach-refresh"
                :disabled="refreshingInsight"
                title="Réanalyser"
                @click="refreshCoachInsight"
              >
                {{ refreshingInsight ? '…' : '↻' }}
              </button>
            </div>
            <p
              v-if="currentLessonInsight.remark"
              class="fh-coach-remark"
              v-html="formatInline(currentLessonInsight.remark)"
            />
            <p
              v-if="currentLessonInsight.diagnosis"
              class="fh-coach-diagnosis"
              v-html="formatInline(currentLessonInsight.diagnosis)"
            />
            <div
              v-if="Array.isArray(currentLessonInsight.weakConcepts) && currentLessonInsight.weakConcepts.length"
              class="fh-coach-chips"
            >
              <span
                v-for="concept in currentLessonInsight.weakConcepts"
                :key="`weak-${concept}`"
                class="fh-coach-chip weak"
                :title="concept"
              >
                <span class="fh-coach-chip-label">À revoir</span>
                <span
                  class="fh-coach-chip-body"
                  v-html="formatInline(concept)"
                />
              </span>
            </div>
            <div
              v-if="Array.isArray(currentLessonInsight.strongConcepts) && currentLessonInsight.strongConcepts.length"
              class="fh-coach-chips"
            >
              <span
                v-for="concept in currentLessonInsight.strongConcepts"
                :key="`strong-${concept}`"
                class="fh-coach-chip strong"
                :title="concept"
              >
                <span class="fh-coach-chip-label">Acquis</span>
                <span
                  class="fh-coach-chip-body"
                  v-html="formatInline(concept)"
                />
              </span>
            </div>
            <p
              v-if="currentLessonInsight.suggestion"
              class="fh-coach-suggestion"
              v-html="formatInline(currentLessonInsight.suggestion)"
            />
            <div
              v-if="currentLessonInsight.recommendedPrompt"
              class="fh-coach-cta"
            >
              <button
                type="button"
                class="fh-btn ghost compact"
                @click="askNickFromInsight"
              >
                Demander à Nick
              </button>
              <span
                class="fh-coach-prompt-preview"
                v-html="formatInline(currentLessonInsight.recommendedPrompt)"
              />
            </div>
            <div
              v-if="pinnedLessonId && pinnedLessonId === currentLesson.lessonId"
              class="fh-coach-continue"
            >
              <button
                type="button"
                class="fh-btn primary compact"
                @click="continueAfterInsight"
              >
                {{ nextLessonCtaLabel(nextLessonAfterPinned) }}
              </button>
              <span
                v-if="nextLessonAfterPinned"
                class="fh-coach-continue-hint"
                v-html="formatInline(nextLessonAfterPinned.title)"
              />
            </div>
          </section>

          <section
            v-if="currentLesson.transition && currentLessonAllAnswered"
            class="fh-lesson-block transition"
          >
            <div
              class="fh-rich-body"
              v-html="formatRich(currentLesson.transition)"
            />
          </section>

          <section
            v-if="currentLesson.narrative || (currentLesson.keyPoints && currentLesson.keyPoints.length)"
            class="fh-lesson-block mindmap-block"
          >
            <span class="fh-block-kicker">Vue globale</span>
            <p class="fh-mindmap-hint">
              Visualise les idées clés de ce chapitre en une carte mentale.
            </p>
            <button
              type="button"
              class="fh-btn primary compact"
              :disabled="generatingMindmap"
              @click="generateLessonMindmap"
            >
              {{ generatingMindmap ? 'Génération…' : 'Générer la carte mentale' }}
            </button>
          </section>

          <div class="fh-lesson-actions">
            <button
              type="button"
              class="fh-btn ghost"
              @click="askNickAboutLesson"
            >
              Demander à Nick
            </button>
            <button
              v-if="currentLesson.status !== 'completed'"
              type="button"
              class="fh-btn primary"
              :disabled="completingLesson || (currentLesson.practice.length > 0 && !currentLessonAllAnswered)"
              @click="completeCurrentLesson"
            >
              {{ completingLesson ? 'Un instant…' : completeLessonButtonLabel(currentLesson) }}
            </button>
            <span
              v-else
              class="fh-lesson-done"
            >
              ✓ Leçon terminée ({{ (currentLesson.progress && currentLesson.progress.lastScore) || 0 }}%)
            </span>
          </div>

          <div
            v-if="lessonPlan.outro && coreLessons.length > 0 && lessonPlanProgress.completed === coreLessons.length"
            class="fh-intro-card outro"
          >
            <span class="fh-intro-tag">Bilan</span>
            <div
              class="fh-rich-body"
              v-html="formatRich(lessonPlan.outro)"
            />
          </div>
        </div>

        <div
          v-else-if="!lessonPlanReady && !lessonPlanGenerating && selectedCourseId"
          class="fh-lesson-intro"
        >
          <div class="fh-intro-card big">
            <span class="fh-intro-tag">Parcours guidé</span>
            <h1>Génère ton parcours avec Nick</h1>
            <p>
              Nick compose un cours narré sur mesure à partir de ton contenu : leçons successives avec récit,
              points clés, exemples et questions pratiques au fur et à mesure.
            </p>
            <p
              v-if="planError"
              class="fh-plan-error"
            >
              {{ planError }}
            </p>
            <button
              type="button"
              class="fh-btn primary"
              :disabled="generatingPlan"
              @click="generateLessonPlan"
            >
              {{ generatingPlan ? 'Nick prépare ton parcours…' : (lessonPlanLocked ? 'Débloquer mon parcours' : 'Générer mon parcours') }}
            </button>
            <p
              v-if="lessonPlanLocked"
              class="fh-lock-hint"
            >
              La génération de parcours fait partie de l'offre TUT'TOP. Abonne-toi pour débloquer Nick et créer ton cours sur mesure.
            </p>
          </div>
        </div>

      </main>

      <aside class="fh-coach">
        <div class="fh-coach-head">
          <div class="fh-coach-title">
            Nick
          </div>
          <div class="fh-coach-sub">
            Ton coach
          </div>
        </div>
        <div class="fh-coach-body">
          <StudentChatbot
            v-if="selectedCourseId && currentUserId && activeCourse"
            :key="`journey-bot-${selectedCourseId}`"
            ref="chatbot"
            :course-id="selectedCourseId"
            :course-title="activeCourse.title"
            :student-id="currentUserId"
            :embedded="true"
            :fill-height="true"
            :brand-name="'Nick'"
            :brand-subtitle="''"
            :custom-starter-prompts="coachPrompts"
            :request-context="chatbotJourneyContext"
          />
        </div>
      </aside>

      <ExerciseGeneratorFlow
        v-if="selectedCourseId && currentUserId && activeCourse"
        :key="`journey-flow-${selectedCourseId}`"
        ref="exerciseFlow"
        :course-id="selectedCourseId"
        :course-title="activeCourse.title"
        :course-subject="activeCourse.subject || ''"
        :created-by="currentUserId"
        @open-quiz="handleExerciseQuiz"
        @open-flashcards="handleExerciseFlashcards"
        @artifact-saved="handleExerciseArtifactSaved"
      />

      <div
        v-if="showProfileModal"
        class="fh-modal-overlay"
        @click.self="closeProfileModal"
      >
        <div class="fh-modal">
          <header class="fh-modal-head">
            <h3>Ajuster mon profil</h3>
            <button
              type="button"
              class="fh-modal-close"
              @click="closeProfileModal"
            >
              ×
            </button>
          </header>

          <div class="fh-modal-body">
            <label class="fh-field">
              <span>Objectif</span>
              <select v-model="profileDraft.objectiveType">
                <option value="mastery">
                  Maîtriser le sujet
                </option>
                <option value="exam">
                  Préparer un examen
                </option>
                <option value="grade">
                  Viser une bonne note
                </option>
                <option value="project">
                  Réaliser un projet
                </option>
              </select>
            </label>

            <label class="fh-field">
              <span>Niveau actuel</span>
              <select v-model="profileDraft.currentLevel">
                <option value="beginner">
                  Débutant
                </option>
                <option value="intermediate">
                  Intermédiaire
                </option>
                <option value="advanced">
                  Avancé
                </option>
              </select>
            </label>

            <label class="fh-field">
              <span>Temps hebdo (min)</span>
              <input
                v-model.number="profileDraft.weeklyMinutes"
                type="number"
                min="30"
                max="1200"
                step="15"
              >
            </label>

            <label class="fh-field">
              <span>Style préféré</span>
              <select v-model="profileDraft.preferredStyle">
                <option value="balanced">
                  Équilibré
                </option>
                <option value="practice">
                  Beaucoup de pratique
                </option>
                <option value="theory">
                  Plus de théorie
                </option>
              </select>
            </label>
          </div>

          <footer class="fh-modal-foot">
            <button
              type="button"
              class="fh-btn ghost"
              :disabled="savingProfile"
              @click="closeProfileModal"
            >
              Annuler
            </button>
            <button
              type="button"
              class="fh-btn primary"
              :disabled="savingProfile"
              @click="saveProfileEdits"
            >
              {{ savingProfile ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
          </footer>
        </div>
      </div>

      <JourneyOnboardingModal
        :open="showOnboardingModal"
        :submitting="onboardingSubmitting"
        :course-title="activeCourse ? activeCourse.title : ''"
        @submit="submitOnboarding"
        @close="closeOnboarding"
      />
    </div>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getUserData } from '@/auth/utils'
import StudentChatbot from '@/components/rag-bot/StudentChatbot.vue'
import ExerciseGeneratorFlow from '@/components/ExerciseGeneratorFlow.vue'
import { renderLatex } from '@/libs/katexRender'
import LearningProfileCard from './LearningProfileCard.vue'
import JourneyOnboardingModal from './JourneyOnboardingModal.vue'
import PathAdaptationCard from './PathAdaptationCard.vue'

export default {
  name: 'JourneyPage',
  components: {
    BModal,
    StudentChatbot,
    ExerciseGeneratorFlow,
    LearningProfileCard,
    JourneyOnboardingModal,
    PathAdaptationCard,
  },
  props: {
    previewCourseId: {
      type: String,
      default: '',
    },
    previewApiBase: {
      type: String,
      default: '',
    },
    embeddedPreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingCourses: false,
      loadingJourney: false,
      rebuilding: false,
      primaryBusy: false,
      coursesError: '',
      courses: [],
      selectedCourseId: '',
      journeyPayload: null,
      activeStepId: '',
      artifactPreview: null,
      artifactDocument: null,
      artifactLoading: false,
      artifactError: '',
      showProfileModal: false,
      savingProfile: false,
      profileDraft: {
        objectiveType: 'mastery',
        currentLevel: 'intermediate',
        weeklyMinutes: 120,
        preferredStyle: 'balanced',
      },
      toolsMode: false,
      generatingPlan: false,
      planError: '',
      answeringQuestion: {},
      answerDrafts: {},
      answerFeedback: {},
      retryingQuestion: {},
      wrongOptionHistory: {},
      completingLesson: false,
      generatingMindmap: false,
      showRemoveCourseModal: false,
      removingCourse: false,
      questionStartTimes: {},
      refreshingInsight: false,
      pinnedLessonId: '',
      learnerProfile: null,
      learnerProfileLoading: false,
      showOnboardingModal: false,
      onboardingSubmitting: false,
      onboardingShownForCourse: '',
    }
  },
  computed: {
    currentUserId() {
      const userData = getUserData() || {}
      const mongoIdKey = '_id'
      return userData.id || userData[mongoIdKey] || null
    },
    selectedCourse() {
      return this.courses.find(course => this.getEntityId(course) === this.selectedCourseId) || null
    },
    // Racine d'API pour la journey : aperçu tuteur (espace) ou parcours élève (cours).
    journeyApiBase() {
      if (this.embeddedPreview) return this.previewApiBase
      return `/api/journey/${this.selectedCourseId}`
    },
    // Vrai pour les nouveaux comptes sans abonnement actif : la génération de
    // parcours est verrouillée et renvoie vers l'achat de l'offre.
    lessonPlanLocked() {
      return !!(this.journeyPayload && this.journeyPayload.lessonPlanLocked)
    },
    activeCourse() {
      return (this.journeyPayload && this.journeyPayload.course) || this.selectedCourse
    },
    profile() {
      return this.journeyPayload && this.journeyPayload.profile ? this.journeyPayload.profile : null
    },
    journey() {
      return this.journeyPayload && this.journeyPayload.journey ? this.journeyPayload.journey : null
    },
    journeySteps() {
      return this.journey && Array.isArray(this.journey.steps) ? this.journey.steps : []
    },
    activeStep() {
      if (!this.journeySteps.length) return null

      const preferredId = this.activeStepId || (this.journey && this.journey.currentStepId)
      if (preferredId) {
        const found = this.journeySteps.find(step => step.stepId === preferredId)
        if (found) return found
      }

      return this.journeySteps[0]
    },
    activeStepTag() {
      if (!this.activeStep) return ''
      const map = {
        profile: 'Profil',
        material: 'Cours',
        generator: 'Générateur',
        artifact: 'Ressource',
        coach: 'Coach',
      }
      return map[this.activeStep.type] || ''
    },
    goalLabel() {
      const raw = this.profile && this.profile.goal && this.profile.goal.targetLabel
        ? String(this.profile.goal.targetLabel).trim()
        : ''
      if (!raw) return ''
      const key = raw.toLowerCase()
      const dict = {
        'learn-ai': "Apprendre avec l'IA",
        learn: 'Apprendre',
        mastery: 'Maîtriser le sujet',
        exam: 'Préparer un examen',
        review: 'Réviser pour un contrôle',
        practice: 'Pratiquer régulièrement',
        discover: 'Découvrir un sujet',
        certification: 'Obtenir une certification',
        retention: 'Améliorer la mémorisation',
      }
      return dict[key] || raw
    },
    coachPrompts() {
      const prompts = []
      const weakArea = this.profile && this.profile.weakAreas && this.profile.weakAreas[0]
      const adaptiveConcept = this.currentLesson && this.currentLesson.adaptiveConceptLabel
        ? this.currentLesson.adaptiveConceptLabel
        : ''
      const basePrompts = this.journey && Array.isArray(this.journey.quickPrompts) ? this.journey.quickPrompts : []
      const stepPrompts = this.activeStep && this.activeStep.meta && Array.isArray(this.activeStep.meta.prompts)
        ? this.activeStep.meta.prompts
        : []

      basePrompts.forEach(prompt => prompts.push(prompt))
      stepPrompts.forEach(prompt => prompts.push(prompt))

      if (weakArea) prompts.push(`Explique ${weakArea} avec un exemple simple.`)
      if (adaptiveConcept) prompts.push(`Explique-moi encore « ${adaptiveConcept} » avant qu'on continue.`)
      if (this.activeStep) prompts.push(`Interroge-moi sur ${this.activeStep.topicLabel || this.activeStep.title}.`)
      prompts.push('Que dois-je faire ensuite ?')

      return [...new Set(prompts.filter(Boolean))].slice(0, 6)
    },
    chatbotJourneyContext() {
      const currentStep = this.activeStep
        ? {
          title: this.activeStep.title,
          topicLabel: this.activeStep.topicLabel,
          type: this.activeStep.type,
          difficulty: this.activeStep.difficulty,
          reason: this.activeStep.reason,
        }
        : null
      const currentIndex = this.activeStep
        ? this.journeySteps.findIndex(step => step.stepId === this.activeStep.stepId)
        : -1
      const nextStep = currentIndex >= 0 && this.journeySteps[currentIndex + 1]
        ? {
          title: this.journeySteps[currentIndex + 1].title,
          topicLabel: this.journeySteps[currentIndex + 1].topicLabel,
          type: this.journeySteps[currentIndex + 1].type,
        }
        : null

      const currentLesson = this.currentLesson
        ? {
          title: this.currentLesson.title,
          subtitle: this.currentLesson.subtitle,
          narrative: this.currentLesson.narrative,
          whyItMatters: this.currentLesson.whyItMatters,
          keyPoints: this.currentLesson.keyPoints,
          order: this.currentLesson.order,
          lessonType: this.currentLesson.lessonType,
          adaptiveKind: this.currentLesson.adaptiveKind,
          adaptiveConceptLabel: this.currentLesson.adaptiveConceptLabel,
          decisionRationale: this.currentLesson.decisionRationale,
        }
        : null

      const learnerStyle = this.learnerProfile
        ? {
          learningMode: this.learnerProfile.learningMode || 'balanced',
          explanationStyle: this.learnerProfile.explanationStyle || 'step_by_step',
          supportLevel: this.learnerProfile.supportLevel || 'medium',
          tone: this.learnerProfile.tone || 'neutral',
        }
        : null

      let weakAreas = []
      if (this.learnerProfile && Array.isArray(this.learnerProfile.weakAreas)) weakAreas = this.learnerProfile.weakAreas
      else if (this.profile && Array.isArray(this.profile.weakAreas)) weakAreas = this.profile.weakAreas

      let strongAreas = []
      if (this.learnerProfile && Array.isArray(this.learnerProfile.strongAreas)) strongAreas = this.learnerProfile.strongAreas
      else if (this.profile && Array.isArray(this.profile.strongAreas)) strongAreas = this.profile.strongAreas

      return {
        courseTitle: this.activeCourse && this.activeCourse.title ? this.activeCourse.title : '',
        summary: this.journey && this.journey.summary ? this.journey.summary : '',
        currentLevel: (this.learnerProfile && this.learnerProfile.currentLevel)
          || (this.profile && this.profile.currentLevel)
          || '',
        weakAreas,
        strongAreas,
        goal: (this.learnerProfile && this.learnerProfile.goal)
          || (this.profile && this.profile.goal)
          || {},
        progress: this.journey && this.journey.progress ? this.journey.progress : {},
        currentStep,
        nextStep,
        currentLesson,
        learnerStyle,
      }
    },
    lessonPlan() {
      return this.journey && this.journey.lessonPlan ? this.journey.lessonPlan : null
    },
    latestPathEvent() {
      const event = this.lessonPlan && this.lessonPlan.lastOrchestration
      return event && event.kind ? event : null
    },
    recentPathEvents() {
      const history = this.lessonPlan && Array.isArray(this.lessonPlan.adaptationHistory)
        ? this.lessonPlan.adaptationHistory
        : []
      return history.slice().reverse().slice(0, 4)
    },
    lessonPlanReady() {
      return !!(this.lessonPlan && this.lessonPlan.status === 'ready' && Array.isArray(this.lessonPlan.lessons) && this.lessonPlan.lessons.length > 0)
    },
    lessonPlanGenerating() {
      return !!(this.lessonPlan && this.lessonPlan.status === 'generating') || this.generatingPlan
    },
    lessons() {
      const list = this.lessonPlan && Array.isArray(this.lessonPlan.lessons) ? this.lessonPlan.lessons.slice() : []
      return list.sort((a, b) => {
        const orderA = Number(a && a.order)
        const orderB = Number(b && b.order)
        if (orderA !== orderB) return orderA - orderB
        return String(a && a.lessonId).localeCompare(String(b && b.lessonId))
      })
    },
    coreLessons() {
      return this.lessons.filter(lesson => !this.isAdaptiveLesson(lesson))
    },
    currentLesson() {
      if (!this.lessonPlanReady) return null
      if (this.pinnedLessonId) {
        const pinned = this.lessons.find(lesson => lesson.lessonId === this.pinnedLessonId)
        if (pinned) return pinned
      }
      const currentId = this.lessonPlan.currentLessonId
      return this.lessons.find(lesson => lesson.lessonId === currentId)
        || this.lessons.find(lesson => lesson.status === 'in_progress')
        || this.lessons[0]
        || null
    },
    nextLessonAfterPinned() {
      if (!this.pinnedLessonId || !this.lessonPlanReady) return null
      const idx = this.lessons.findIndex(l => l.lessonId === this.pinnedLessonId)
      if (idx < 0 || idx >= this.lessons.length - 1) return null
      return this.lessons[idx + 1]
    },
    lessonPlanProgress() {
      const total = this.coreLessons.length
      const completed = this.coreLessons.filter(lesson => lesson.status === 'completed').length
      return {
        total,
        completed,
        percent: total > 0 ? Math.round((completed / total) * 100) : 0,
      }
    },
    currentLessonAllAnswered() {
      const lesson = this.currentLesson
      if (!lesson || !Array.isArray(lesson.practice) || lesson.practice.length === 0) return false
      return lesson.progress && lesson.progress.answeredCount >= lesson.practice.length
    },
    lessonPlanVersion() {
      return Number(this.lessonPlan && this.lessonPlan.version) || 1
    },
    lessonPlanNeedsUpgrade() {
      return this.lessonPlanReady && this.lessonPlanVersion < 5
    },
    currentLessonInsight() {
      const lesson = this.currentLesson
      if (!lesson || !lesson.coachInsight) return null
      const insight = lesson.coachInsight
      if (!insight.status || insight.status === 'idle') return null
      return insight
    },
    insightToneLabel() {
      const tone = this.currentLessonInsight && this.currentLessonInsight.tone
      return {
        positive: 'Bravo',
        neutral: 'Remarque',
        careful: 'Attention',
        alert: 'À consolider',
      }[tone] || 'Remarque'
    },
    currentLessonAnsweredCount() {
      const lesson = this.currentLesson
      if (!lesson || !lesson.progress) return 0
      return Number(lesson.progress.answeredCount || 0)
    },
    displayCourseTitle() {
      let raw = ''
      if (this.activeCourse && this.activeCourse.title) {
        raw = this.activeCourse.title
      } else if (this.journey && this.journey.title) {
        raw = this.journey.title
      }
      return this.humanizeLabel(raw || 'Parcours guidé')
    },
    displayJourneySummary() {
      const level = this.profile && this.profile.currentLevel
        ? {
          beginner: 'débutant',
          intermediate: 'intermédiaire',
          advanced: 'avancé',
        }[this.profile.currentLevel] || this.profile.currentLevel
        : ''
      const weakArea = this.profile && Array.isArray(this.profile.weakAreas) && this.profile.weakAreas[0]
        ? this.profile.weakAreas[0]
        : ''

      if (weakArea && level) {
        return `Parcours guidé sur ${this.displayCourseTitle}, avec un focus sur ${weakArea} et un niveau ${level}.`
      }
      if (level) {
        return `Parcours guidé sur ${this.displayCourseTitle}, adapté à ton niveau ${level}.`
      }
      return `Parcours guidé personnalisé sur ${this.displayCourseTitle}.`
    },
    currentLessonOutcomes() {
      if (!this.currentLesson) return []
      const outcomes = Array.isArray(this.currentLesson.outcomes) ? this.currentLesson.outcomes.filter(Boolean) : []
      if (outcomes.length) return outcomes.slice(0, 3)
      const keyPoints = Array.isArray(this.currentLesson.keyPoints) ? this.currentLesson.keyPoints.filter(Boolean) : []
      return keyPoints.slice(0, 3).map(point => this.outcomeFromKeyPoint(point)).filter(Boolean)
    },
    currentLessonFormula() {
      if (!this.currentLesson) return null
      if (this.currentLesson.formula && this.currentLesson.formula.latex) {
        return this.currentLesson.formula
      }

      const sources = [
        this.currentLesson.example && this.currentLesson.example.body,
        this.currentLesson.narrative,
        this.currentLesson.whyItMatters,
        ...(Array.isArray(this.currentLesson.keyPoints) ? this.currentLesson.keyPoints : []),
      ].filter(Boolean)

      for (let index = 0; index < sources.length; index += 1) {
        const latex = this.extractFormulaCandidate(sources[index])
        if (latex) {
          return {
            title: 'Formule clé',
            latex,
            explanation: 'Garde cette relation en tête pendant les exercices.',
          }
        }
      }

      return null
    },
    showLessonMode() {
      return this.lessonPlanReady && !this.toolsMode
    },
    stepSections() {
      if (!this.activeStep) return []

      if (this.activeStep.type === 'profile' && this.profile) {
        const sections = []
        const { goal } = this.profile
        const levelLabels = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' }
        const goalLines = []
        if (this.goalLabel) goalLines.push(`Objectif : ${this.goalLabel}`)
        if (goal && goal.weeklyMinutes) goalLines.push(`Temps cible : ${goal.weeklyMinutes} min / semaine`)
        if (this.profile.currentLevel) {
          const humanLevel = levelLabels[this.profile.currentLevel] || this.profile.currentLevel
          goalLines.push(`Niveau : ${humanLevel}`)
        }

        if (goalLines.length) {
          sections.push({
            title: 'Objectif',
            kind: 'text',
            lines: goalLines,
          })
        }

        const weakAreas = Array.isArray(this.profile.weakAreas) ? this.profile.weakAreas : []
        if (weakAreas.length) {
          sections.push({
            title: 'À renforcer',
            kind: 'list',
            items: weakAreas,
          })
        }

        const strongAreas = Array.isArray(this.profile.strongAreas) ? this.profile.strongAreas : []
        if (strongAreas.length) {
          sections.push({
            title: 'Acquis',
            kind: 'list',
            items: strongAreas,
          })
        }

        return sections
      }

      if (this.activeStep.type === 'material') {
        const sections = []
        if (this.activeStep.description) {
          sections.push({
            title: 'À propos',
            kind: 'text',
            lines: [this.activeStep.description],
          })
        }
        if (this.activeStep.topicLabel) {
          sections.push({
            title: 'Thème',
            kind: 'text',
            lines: [this.activeStep.topicLabel],
          })
        }
        return sections
      }

      if (this.activeStep.type === 'generator') {
        return [{
          title: 'Ce que nous allons générer',
          kind: 'text',
          lines: [
            this.activeStep.description || 'Génère une ressource d\'apprentissage adaptée à ton profil.',
          ],
        }]
      }

      if (this.activeStep.type === 'coach') {
        return [{
          title: 'Idées de questions',
          kind: 'prompts',
          prompts: this.coachPrompts,
        }]
      }

      if (this.activeStep.type === 'artifact') {
        if (this.artifactLoading) {
          return [{
            title: 'Aperçu',
            kind: 'state',
            message: 'Chargement de la ressource…',
          }]
        }

        if (this.artifactError) {
          return [{
            title: 'Aperçu',
            kind: 'state',
            message: this.artifactError,
          }]
        }

        if (!this.artifactPreview) return []

        if (this.activeStep.artifactType === 'notes') {
          const sections = []
          const noteSections = Array.isArray(this.artifactPreview.sections)
            ? this.artifactPreview.sections.slice(0, 4)
            : []
          if (noteSections.length) {
            sections.push({
              title: 'Chapitres',
              kind: 'notes',
              sections: noteSections,
            })
          }
          const keyTerms = Array.isArray(this.artifactPreview.keyTerms)
            ? this.artifactPreview.keyTerms
              .map(term => (typeof term === 'string' ? term : term && term.term))
              .filter(Boolean)
              .slice(0, 8)
            : []
          if (keyTerms.length) {
            sections.push({
              title: 'Termes clés',
              kind: 'list',
              items: keyTerms,
            })
          }
          return sections
        }

        if (this.activeStep.artifactType === 'flashcards') {
          const cards = Array.isArray(this.artifactPreview.cards)
            ? this.artifactPreview.cards.slice(0, 4)
            : []
          return [{
            title: 'Aperçu',
            kind: 'flashcards',
            cards,
          }]
        }

        if (this.activeStep.artifactType === 'quiz') {
          const questions = Array.isArray(this.artifactPreview.questions)
            ? this.artifactPreview.questions.slice(0, 3)
            : []
          return [{
            title: 'Aperçu',
            kind: 'quiz',
            questions,
          }]
        }

        if (this.activeStep.artifactType === 'exercises') {
          return [{
            title: 'Aperçu',
            kind: 'exercises',
            payload: this.artifactPreview,
          }]
        }
      }

      return []
    },
    primaryAction() {
      if (!this.activeStep) return null

      if (this.activeStep.type === 'profile') {
        return { id: 'edit-profile', label: 'Ajuster le profil' }
      }

      if (this.activeStep.type === 'material') {
        const meta = this.activeStep.meta || {}
        if (meta.fileUrl || meta.externalUrl) {
          return { id: 'open-material', label: 'Ouvrir le support' }
        }
      }

      if (this.activeStep.type === 'generator') {
        if (this.activeStep.actionType === 'generate-notes') {
          return { id: 'generate-notes', label: 'Générer la fiche' }
        }
        if (this.activeStep.actionType === 'generate-flashcards') {
          return { id: 'generate-flashcards', label: 'Générer des flashcards' }
        }
        if (this.activeStep.actionType === 'generate-quiz') {
          return { id: 'generate-quiz', label: 'Générer un quiz' }
        }
        if (this.activeStep.actionType === 'generate-practice') {
          return { id: 'generate-practice', label: 'Générer des exercices' }
        }
      }

      if (this.activeStep.type === 'artifact') {
        return { id: 'open-artifact', label: 'Ouvrir avec Nick' }
      }

      if (this.activeStep.type === 'coach') {
        return { id: 'open-coach', label: 'Ouvrir Nick' }
      }

      return null
    },
    secondaryAction() {
      if (!this.activeStep) return null

      if (this.activeStep.type === 'profile') {
        return { id: 'refresh', label: 'Recalculer le plan' }
      }
      if (this.activeStep.type === 'material') {
        return { id: 'explain', label: 'Demander une explication à Nick' }
      }
      if (this.activeStep.type === 'generator') {
        return { id: 'prime', label: 'Préparer avec Nick' }
      }
      if (this.activeStep.type === 'artifact') {
        return { id: 'coach-help', label: 'Me coacher dessus' }
      }
      return null
    },
  },
  watch: {
    '$route.params.courseId': {
      immediate: true,
      handler() {
        this.bootstrapJourney()
      },
    },
    'currentLesson.lessonId': {
      immediate: true,
      handler() {
        this.questionStartTimes = {}
        this.$nextTick(() => this.initTimersForLesson())
      },
    },
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'dashboard-student-personal-courses' }).catch(() => {})
      }
    },
    toast(title, text, variant = 'primary', icon = 'InfoIcon') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          variant,
          icon,
        },
      })
    },
    getEntityId(entity) {
      if (!entity) return ''
      const mongoIdKey = '_id'
      return String(entity.id || entity[mongoIdKey] || '')
    },
    humanizeLabel(value) {
      const str = String(value || '').trim().replace(/\s+/g, ' ')
      if (!str) return ''
      return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
    },
    outcomeFromKeyPoint(value) {
      const point = String(value || '').trim()
      if (!point) return ''
      const cleaned = point.replace(/^[•-]\s*/, '').replace(/[.?!]+$/g, '')
      return cleaned ? `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}` : ''
    },
    stepIcon(step) {
      if (!step) return '○'
      if (step.status === 'completed') return '●'
      if (step.status === 'in_progress') return '◉'
      return '○'
    },
    isAdaptiveLesson(lesson) {
      return !!(lesson && lesson.lessonType === 'adaptive')
    },
    adaptiveKindLabel(lesson) {
      const kind = typeof lesson === 'string' ? lesson : lesson && lesson.adaptiveKind
      return {
        remediation: 'Renfort',
        reexplain: 'Renfort',
        challenge: 'Challenge',
        practice_boost: 'Pratique ciblée',
        spaced_review: 'Révision',
      }[kind] || 'Étape'
    },
    lessonDisplayTitle(lesson) {
      if (!lesson) return ''
      const title = String(lesson.title || '').trim()
      if (!this.isAdaptiveLesson(lesson)) return title
      if (lesson.adaptiveKind === 'reexplain' && ['Autre explication', 'Autre angle', 'Point rapide'].includes(title)) {
        return 'Renfort'
      }
      if (lesson.adaptiveKind === 'remediation' && ['Renfort ciblé'].includes(title)) {
        return 'Renfort'
      }
      return title || this.adaptiveKindLabel(lesson)
    },
    lessonDisplayIndex(lesson) {
      if (!lesson) return 1
      const raw = Number.isFinite(Number(lesson.curriculumOrder))
        ? Number(lesson.curriculumOrder)
        : Number(lesson.order)
      return Math.max(1, Math.round(raw) + 1)
    },
    lessonBadgeLabel(lesson) {
      if (!lesson) return ''
      return this.isAdaptiveLesson(lesson) ? 'A' : this.lessonDisplayIndex(lesson)
    },
    lessonKicker(lesson) {
      if (!lesson) return ''
      if (this.isAdaptiveLesson(lesson)) {
        return `Étape adaptative · ${this.adaptiveKindLabel(lesson)}`
      }
      return `Leçon ${this.lessonDisplayIndex(lesson)} sur ${this.coreLessons.length}`
    },
    toLatexMath(rawExpression) {
      return String(rawExpression || '')
        .trim()
        .replace(/(\d),(\d)/g, '$1.$2')
        .replace(/\s*\*\s*/g, ' \\cdot ')
        .replace(/λ̂/g, '\\hat{\\lambda}')
        .replace(/θ̂/g, '\\hat{\\theta}')
        .replace(/X̄/g, '\\bar{X}')
        .replace(/x̄/g, '\\bar{x}')
        .replace(/μ/g, '\\mu')
        .replace(/σ/g, '\\sigma')
        .replace(/θ/g, '\\theta')
        .replace(/λ/g, '\\lambda')
        .replace(/π/g, '\\pi')
        .replace(/≤/g, '\\le ')
        .replace(/≥/g, '\\ge ')
        .replace(/≈/g, '\\approx ')
        .replace(/−/g, '-')
    },
    extractFormulaCandidate(text) {
      const source = String(text || '')
      if (!source) return ''

      const blockMatch = source.match(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/)
      if (blockMatch) return (blockMatch[1] || blockMatch[2] || '').trim()

      const inlineMatch = source.match(/\$([^$\n]+)\$|\\\(([\s\S]+?)\\\)/)
      if (inlineMatch) return (inlineMatch[1] || inlineMatch[2] || '').trim()

      const plainEquation = source.match(/(?:L\([^)]+\)|E\[[^\]]+\]|Var\([^)]+\)|Cov\([^)]+\)|P\([^)]+\)|Z|T|X̄[_a-zA-Z0-9]*|x̄[_a-zA-Z0-9]*|λ̂[_a-zA-Z0-9]*|θ̂[_a-zA-Z0-9]*|[λθμσπ][A-Za-z0-9_]*)\s*=\s*[^.;:\n]+/u)
      return plainEquation ? plainEquation[0].trim() : ''
    },
    renderFormulaBlock(latex) {
      if (!latex) return ''
      const clean = this.toLatexMath(latex)
      return renderLatex(`$$${clean}$$`)
    },
    wrapLikelyMath(text) {
      if (!text) return ''

      const normalizeLatex = rawExpression => {
        const expr = String(rawExpression || '').trim()
        if (!expr || expr.includes('$') || expr.includes('__MATH_')) return rawExpression

        const looksLikeMath = /[=^λθμσπΔΣΠ]/u.test(expr)
          || /\u0302|\u0304/u.test(expr)
          || /\b(?:L|E|Var|Cov|P)\s*[[(]/.test(expr)

        if (!looksLikeMath) return rawExpression

        return `$${this.toLatexMath(expr)
          .replace(/Δ/g, '\\Delta')
          .replace(/Σ/g, '\\Sigma')
          .replace(/Π/g, '\\Pi')}$`
      }

      const token = '[\\p{L}_][\\p{L}\\p{N}_]*'
      const specialToken = '(?:λ̂|θ̂|X̄|x̄)[A-Za-z0-9_]*'
      const accessor = `${token}(?:\\([^)]+\\)|\\[[^\\]]+\\])?`
      const powered = `${accessor}(?:\\^[A-Za-z0-9λθμσπΔΣΠ_()+\\-]+)?`
      const number = '\\d+(?:[.,]\\d+)?'
      const atom = `(?:${powered}|${specialToken}|${number}|\\([^)]+\\)|\\[[^\\]]+\\])`
      const equation = new RegExp(
        `(^|[\\s:(])((?:L\\([^)]+\\)|E\\[[^\\]]+\\]|Var\\([^)]+\\)|Cov\\([^)]+\\)|P\\([^)]+\\)|${accessor})\\s*=\\s*${atom}(?:\\s*(?:[+\\-/*]|×|·)\\s*${atom})*)(?=$|[\\s,.;:!?)])`,
        'gu',
      )
      const productOrPower = new RegExp(
        `(^|[\\s:(])(${atom}(?:\\s*(?:[+\\-/*]|×|·)\\s*${atom})+)(?=$|[\\s,.;:!?)])`,
        'gu',
      )

      return String(text)
        .replace(equation, (_, prefix, expr) => `${prefix}${normalizeLatex(expr)}`)
        .replace(productOrPower, (_, prefix, expr) => `${prefix}${normalizeLatex(expr)}`)
    },
    formatInlineMarkdown(text) {
      if (!text) return ''

      const mathPlaceholders = []
      let placeholderIndex = 0

      let html = String(text).replace(/\$\$([\s\S]*?)\$\$/g, match => {
        const placeholder = `__MATH_BLOCK_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      html = html.replace(/\$([^$\n]+)\$/g, match => {
        const placeholder = `__MATH_INLINE_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      html = html.replace(/\\\(([\s\S]*?)\\\)/g, match => {
        const placeholder = `__MATH_INLINE_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      html = html.replace(/\\\[([\s\S]*?)\\\]/g, match => {
        const placeholder = `__MATH_BLOCK_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      html = this.wrapLikelyMath(html)

      html = html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

      mathPlaceholders.forEach((formula, index) => {
        html = html.replace(`__MATH_BLOCK_${index}__`, formula)
        html = html.replace(`__MATH_INLINE_${index}__`, formula)
      })

      html = html.replace(/\*\*\*([^*]+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/(?<!\*)\*([^*$]+?)\*(?!\*)/g, '<em>$1</em>')
      html = html.replace(/`([^`]+)`/g, '<code class="fh-inline-code">$1</code>')

      return html
    },
    formatRich(text) {
      if (!text) return ''
      const mathPlaceholders = []
      let placeholderIndex = 0

      let protectedText = String(text).replace(/\$\$([\s\S]*?)\$\$/g, match => {
        const placeholder = `__MATH_DISPLAY_$$_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      protectedText = protectedText.replace(/\\\[([\s\S]*?)\\\]/g, match => {
        const placeholder = `__MATH_DISPLAY_BRACKET_${placeholderIndex}__`
        mathPlaceholders[placeholderIndex] = match
        placeholderIndex += 1
        return placeholder
      })

      const lines = protectedText.split('\n')
      let html = ''
      let inList = false
      let listItems = []
      const flushList = () => {
        if (inList && listItems.length > 0) {
          html += `<ul class="fh-rich-list">${listItems.join('')}</ul>`
          listItems = []
          inList = false
        }
      }

      for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index].trim()

        if (!line) {
          flushList()
        } else {
          const displayMathMatch = line.match(/^__(MATH_DISPLAY_.*?_(\d+))__$/)

          if (displayMathMatch) {
            flushList()
            html += `<div class="fh-math-display">${mathPlaceholders[displayMathMatch[2]]}</div>`
          } else if (line.startsWith('#### ')) {
            flushList()
            html += `<h4 class="fh-rich-title small">${this.formatInlineMarkdown(line.substring(5))}</h4>`
          } else if (line.startsWith('### ')) {
            flushList()
            html += `<h3 class="fh-rich-title">${this.formatInlineMarkdown(line.substring(4))}</h3>`
          } else if (line.startsWith('## ')) {
            flushList()
            html += `<h2 class="fh-rich-title large">${this.formatInlineMarkdown(line.substring(3))}</h2>`
          } else if (line.startsWith('# ')) {
            flushList()
            html += `<h2 class="fh-rich-title large">${this.formatInlineMarkdown(line.substring(2))}</h2>`
          } else if (line.startsWith('- ') || line.startsWith('* ')) {
            if (!inList) {
              inList = true
            }
            listItems.push(`<li>${this.formatInlineMarkdown(line.substring(2))}</li>`)
          } else {
            const numberedMatch = line.match(/^\d+\.\s+(.+)$/)
            if (numberedMatch) {
              if (!inList) {
                inList = true
              }
              listItems.push(`<li>${this.formatInlineMarkdown(numberedMatch[1])}</li>`)
            } else {
              flushList()
              html += `<p class="fh-rich-paragraph">${this.formatInlineMarkdown(line)}</p>`
            }
          }
        }
      }

      if (inList && listItems.length > 0) {
        html += `<ul class="fh-rich-list">${listItems.join('')}</ul>`
      }

      mathPlaceholders.forEach((formula, index) => {
        html = html.replace(`__MATH_DISPLAY_$$_${index}__`, formula)
        html = html.replace(`__MATH_DISPLAY_BRACKET_${index}__`, formula)
      })

      return renderLatex(html)
    },
    formatInline(text) {
      return renderLatex(this.formatInlineMarkdown(text))
    },
    lessonIcon(lesson) {
      if (!lesson) return '○'
      if (this.isAdaptiveLesson(lesson) && lesson.status !== 'locked') return '✦'
      if (lesson.status === 'completed') return '●'
      if (lesson.status === 'in_progress') return '◉'
      if (lesson.status === 'locked') return '🔒'
      return '○'
    },
    nextLessonCtaLabel(lesson) {
      if (!lesson) return 'Retourner au parcours'
      if (this.isAdaptiveLesson(lesson)) {
        return `Continuer → ${this.adaptiveKindLabel(lesson)}`
      }
      return `Continuer → Leçon ${this.lessonDisplayIndex(lesson)}`
    },
    completeLessonButtonLabel(lesson) {
      if (!lesson) return 'Continuer'
      const practiceCount = Array.isArray(lesson.practice) ? lesson.practice.length : 0
      if (this.isAdaptiveLesson(lesson)) {
        return {
          remediation: 'Terminer le renfort',
          reexplain: 'Terminer le point rapide',
          challenge: 'Terminer le challenge',
          practice_boost: 'Terminer la pratique ciblée',
          spaced_review: 'Terminer la révision',
        }[lesson.adaptiveKind] || 'Terminer cette étape'
      }
      return practiceCount === 0 ? 'Continuer' : 'Terminer la leçon'
    },
    adaptiveExplainButtonLabel(lesson) {
      if (!lesson) return 'Explique-moi avant de pratiquer'
      const concept = lesson.adaptiveConceptLabel || ''
      if (lesson.adaptiveKind === 'challenge') {
        return concept
          ? `Rappelle-moi « ${concept} » avant le challenge`
          : 'Rappelle-moi l\'idée avant le challenge'
      }
      if (lesson.adaptiveKind === 'practice_boost') {
        return concept
          ? `Rappelle-moi « ${concept} » avant la pratique ciblée`
          : 'Rappelle-moi l\'idée avant la pratique ciblée'
      }
      return concept
        ? `Explique-moi « ${concept} » avant de pratiquer`
        : 'Explique-moi avant de pratiquer'
    },
    explainAdaptiveConcept(lesson) {
      if (!lesson) return
      const kind = lesson.adaptiveKind || 'remediation'
      const concept = lesson.adaptiveConceptLabel || lesson.title || 'cette étape'
      let prompt = `Peux-tu me réexpliquer « ${concept} » simplement, avec un petit exemple, avant que je fasse les exercices de cette étape adaptative ?`
      if (kind === 'challenge') {
        prompt = `Avant le petit challenge sur « ${concept} », peux-tu me rappeler l'idée clé et me donner un conseil pour bien le réussir ?`
      } else if (kind === 'practice_boost') {
        prompt = `Avant la pratique ciblée sur « ${concept} », peux-tu me rappeler la règle et me donner un mini exemple ?`
      } else if (kind === 'reexplain') {
        prompt = `Peux-tu reformuler « ${concept} » autrement, avec un angle différent, avant que je refasse les exercices ?`
      } else if (kind === 'spaced_review') {
        prompt = `Petit rappel avant la révision : peux-tu me résumer « ${concept} » en 2-3 phrases claires et me rappeler une erreur fréquente à éviter ?`
      }
      this.sendCoachPrompt(prompt)
    },
    optionClass(question, optionIndex) {
      const isRetrying = !!this.retryingQuestion[question.questionId]
      const wrongHistory = this.wrongOptionHistory[question.questionId] || []
      if (isRetrying) {
        if (wrongHistory.includes(optionIndex)) return 'wrong muted'
        return ''
      }
      const attempt = this.getQuestionAttempt(question.questionId)
      const feedback = this.getQuestionFeedback(question.questionId)
      if (!attempt && !feedback) return ''
      const correctIndex = feedback && typeof feedback.correctIndex === 'number'
        ? feedback.correctIndex
        : question.correctIndex
      if (optionIndex === correctIndex) return 'correct'
      let submittedIndex = null
      if (feedback && typeof feedback.submittedIndex === 'number') {
        submittedIndex = feedback.submittedIndex
      } else if (attempt && typeof attempt.answerIndex === 'number') {
        submittedIndex = attempt.answerIndex
      }
      if (optionIndex === submittedIndex) return 'wrong'
      return 'muted'
    },
    isQuestionLocked(questionId) {
      if (this.retryingQuestion[questionId]) return false
      return !!this.getQuestionAttempt(questionId)
    },
    isOptionAlreadyTriedWrong(questionId, optionIndex) {
      const history = this.wrongOptionHistory[questionId] || []
      return history.includes(optionIndex)
    },
    canRetryQuestion(question) {
      if (!question || !question.questionId) return false
      if (this.retryingQuestion[question.questionId]) return false
      const feedback = this.getQuestionFeedback(question.questionId)
      if (!feedback || feedback.correct) return false
      const attempt = this.getQuestionAttempt(question.questionId)
      if (!attempt) return false
      const attemptCount = Number(attempt.attemptCount) || 1
      return attemptCount < 2
    },
    retryQuestion(question) {
      if (!question || !question.questionId) return
      if (!this.canRetryQuestion(question)) return
      const { questionId } = question
      const feedback = this.answerFeedback[questionId]
      const history = this.wrongOptionHistory[questionId] || []
      const wrongIndex = feedback && typeof feedback.submittedIndex === 'number'
        ? feedback.submittedIndex
        : null
      const nextHistory = (Number.isInteger(wrongIndex) && !history.includes(wrongIndex))
        ? [...history, wrongIndex]
        : history
      this.wrongOptionHistory = { ...this.wrongOptionHistory, [questionId]: nextHistory }
      this.retryingQuestion = { ...this.retryingQuestion, [questionId]: true }
      const nextFeedback = { ...this.answerFeedback }
      delete nextFeedback[questionId]
      this.answerFeedback = nextFeedback
      const nextDrafts = { ...this.answerDrafts }
      if (question.kind === 'short_answer') nextDrafts[questionId] = ''
      this.answerDrafts = nextDrafts
      this.questionStartTimes = { ...this.questionStartTimes, [questionId]: Date.now() }
    },
    getPreferredStepId(steps, preferredStepKey = '') {
      if (!Array.isArray(steps) || steps.length === 0) return ''
      if (preferredStepKey) {
        const byKey = steps.find(step => step.stepKey === preferredStepKey)
        if (byKey) return byKey.stepId
      }
      const current = steps.find(step => step.status === 'in_progress')
      if (current) return current.stepId
      return steps[0].stepId
    },
    async bootstrapJourney() {
      this.loadingCourses = true
      this.coursesError = ''
      try {
        if (this.embeddedPreview) {
          this.selectedCourseId = this.previewCourseId
          await this.loadJourney(this.previewCourseId)
          await this.loadCourses()
          return
        }
        await this.loadCourses()
        const routeCourseId = this.$route.params.courseId ? String(this.$route.params.courseId) : ''
        const fallbackCourseId = routeCourseId
          || this.selectedCourseId
          || (this.courses[0] ? this.getEntityId(this.courses[0]) : '')

        if (!fallbackCourseId) {
          this.selectedCourseId = ''
          this.journeyPayload = null
          return
        }

        this.selectedCourseId = fallbackCourseId
        await this.loadJourney(fallbackCourseId)
      } catch (error) {
        this.coursesError = error.response?.data?.error || error.message || 'Impossible de charger la journey.'
      } finally {
        this.loadingCourses = false
      }
    },
    async loadCourses() {
      if (this.embeddedPreview) {
        this.courses = this.journeyPayload && this.journeyPayload.course ? [this.journeyPayload.course] : []
        return
      }
      const response = await axios.get('/api/journey/courses')
      this.courses = Array.isArray(response.data?.courses) ? response.data.courses : []
    },
    async loadJourney(courseId, preferredStepKey = '') {
      if (!courseId) return
      this.loadingJourney = true
      this.artifactPreview = null
      this.artifactDocument = null
      this.artifactError = ''

      try {
        const url = this.embeddedPreview ? this.previewApiBase : `/api/journey/${courseId}`
        const response = await axios.get(url)
        this.journeyPayload = response.data
        this.selectedCourseId = courseId
        this.activeStepId = this.getPreferredStepId(this.journeySteps, preferredStepKey)
        if (!this.embeddedPreview) {
          await this.loadActiveArtifactPreview()
        }
        await this.loadLearnerProfile(courseId)
      } catch (error) {
        this.journeyPayload = null
        this.toast('Journey', error.response?.data?.error || 'Impossible de charger cette journey.', 'danger', 'AlertCircleIcon')
      } finally {
        this.loadingJourney = false
      }
    },

    async loadLearnerProfile(courseId) {
      if (!courseId) return
      if (this.embeddedPreview) {
        this.learnerProfile = (this.journeyPayload && this.journeyPayload.profile) || null
        return
      }
      this.learnerProfileLoading = true
      try {
        const response = await axios.get(`/api/journey/${courseId}/learner-profile`)
        this.learnerProfile = (response.data && response.data.profile) || null

        if (
          (!this.learnerProfile || !this.learnerProfile.onboardingCompleted)
          && this.onboardingShownForCourse !== courseId
        ) {
          this.showOnboardingModal = true
          this.onboardingShownForCourse = courseId
        }
      } catch (error) {
        console.warn('[Journey] learner profile fetch failed:', error.message)
        this.learnerProfile = null
      } finally {
        this.learnerProfileLoading = false
      }
    },

    openOnboarding() {
      this.showOnboardingModal = true
    },

    closeOnboarding() {
      this.showOnboardingModal = false
    },

    async submitOnboarding(answers) {
      if (!this.selectedCourseId) {
        this.closeOnboarding()
        return
      }
      this.onboardingSubmitting = true
      try {
        const response = await axios.post(
          `/api/journey/${this.selectedCourseId}/learner-profile/onboard`,
          { answers },
        )
        this.learnerProfile = (response.data && response.data.profile) || null
        this.toast('Personnalisation', 'Profil enregistré. Nick va s\'adapter.', 'success', 'CheckCircleIcon')
        this.closeOnboarding()

        try {
          const refresh = await axios.post(`/api/journey/${this.selectedCourseId}/refresh`, {})
          if (refresh && refresh.data) this.journeyPayload = refresh.data
        } catch (refreshErr) {
          console.warn('[Onboarding] journey refresh failed:', refreshErr.message)
        }
      } catch (error) {
        this.toast(
          'Personnalisation',
          error.response?.data?.error || 'Impossible d\'enregistrer ton profil.',
          'danger',
          'AlertCircleIcon',
        )
      } finally {
        this.onboardingSubmitting = false
      }
    },
    async onCourseChange(courseId) {
      if (!courseId || courseId === this.selectedCourseId) return
      this.selectedCourseId = courseId
      this.activeStepId = ''
      if (this.embeddedPreview) {
        await this.loadJourney(courseId)
        return
      }
      this.$router.push({ name: 'student-journey', params: { courseId } }).catch(() => {})
    },
    setActiveStep(step) {
      if (!step) return
      this.activeStepId = step.stepId
      this.loadActiveArtifactPreview()
    },
    async loadActiveArtifactPreview() {
      this.artifactPreview = null
      this.artifactDocument = null
      this.artifactError = ''

      if (!this.activeStep || this.activeStep.type !== 'artifact' || !this.activeStep.artifactId) return

      this.artifactLoading = true
      try {
        const response = await axios.get(`/study-artifacts/${this.activeStep.artifactId}`)
        const artifact = response.data?.artifact || null
        this.artifactDocument = artifact
        this.artifactPreview = artifact ? artifact.contentJson : null
      } catch (error) {
        this.artifactError = error.response?.data?.error || 'Impossible de charger cet artefact.'
      } finally {
        this.artifactLoading = false
      }
    },
    async refreshCurrentJourney() {
      if (!this.selectedCourseId) return
      this.rebuilding = true
      try {
        if (this.embeddedPreview) {
          const preferredStepKey = this.activeStep ? this.activeStep.stepKey : ''
          await this.loadJourney(this.selectedCourseId, preferredStepKey)
          return
        }
        const preferredStepKey = this.activeStep ? this.activeStep.stepKey : ''
        const response = await axios.post(`/api/journey/${this.selectedCourseId}/refresh`, {})
        this.journeyPayload = response.data
        this.activeStepId = this.getPreferredStepId(this.journeySteps, preferredStepKey)
        await this.loadCourses()
        await this.loadActiveArtifactPreview()
      } catch (error) {
        this.toast('Journey', error.response?.data?.error || 'Impossible de recalculer la journey.', 'danger', 'AlertCircleIcon')
      } finally {
        this.rebuilding = false
      }
    },
    // Ouvre la modale d'abonnement/achat (gérée globalement par App.vue).
    promptParcoursPurchase() {
      this.$store.dispatch('credits/openRechargeModal', {
        message: 'Abonne-toi pour débloquer la génération de ton parcours personnalisé par Nick.',
      }).catch(() => {})
    },
    async generateLessonPlan() {
      if (!this.selectedCourseId || this.generatingPlan) return
      // Nouveaux comptes sans abonnement : on redirige vers l'achat de l'offre
      // plutôt que de lancer la génération.
      if (this.lessonPlanLocked) {
        this.promptParcoursPurchase()
        return
      }
      this.generatingPlan = true
      this.planError = ''
      try {
        const response = await axios.post(`${this.journeyApiBase}/lesson-plan/generate`)
        if (response.data && response.data.journey) {
          this.journeyPayload = {
            ...(this.journeyPayload || {}),
            journey: response.data.journey,
          }
        }
        this.answerDrafts = {}
        this.answerFeedback = {}
        this.retryingQuestion = {}
        this.wrongOptionHistory = {}
        this.toolsMode = false
        this.toast('Parcours prêt', 'Nick a préparé ton cours guidé.', 'success', 'BookOpenIcon')
      } catch (error) {
        // Garde-fou serveur : compte verrouillé -> la modale d'achat est déjà
        // ouverte par l'intercepteur axios global, on n'affiche pas d'erreur.
        if (error.response && error.response.status === 402
          && error.response.data && error.response.data.error === 'SUBSCRIPTION_REQUIRED') {
          return
        }
        const message = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'La génération du parcours a échoué.'
        this.planError = message
        this.toast('Parcours guidé', message, 'danger', 'AlertCircleIcon')
      } finally {
        this.generatingPlan = false
      }
    },
    async selectLesson(lesson) {
      if (!lesson || !this.selectedCourseId) return
      if (lesson.status === 'locked') {
        this.toast('Leçon verrouillée', 'Termine la leçon en cours pour débloquer la suivante.', 'warning', 'LockIcon')
        return
      }
      this.pinnedLessonId = lesson.lessonId === this.pinnedLessonId ? this.pinnedLessonId : ''
      try {
        const response = await axios.post(
          `${this.journeyApiBase}/lesson-plan/lessons/${lesson.lessonId}/select`,
        )
        if (response.data && response.data.journey) {
          this.journeyPayload = {
            ...(this.journeyPayload || {}),
            journey: response.data.journey,
          }
        }
      } catch (error) {
        this.toast('Leçon', error.response?.data?.error || 'Impossible d\'ouvrir la leçon.', 'danger', 'AlertCircleIcon')
      }
    },
    getQuestionAttempt(questionId) {
      const lesson = this.currentLesson
      if (!lesson || !lesson.progress || !Array.isArray(lesson.progress.attempts)) return null
      return lesson.progress.attempts.find(attempt => attempt.questionId === questionId) || null
    },
    getQuestionFeedback(questionId) {
      return this.answerFeedback[questionId] || null
    },
    setAnswerDraft(questionId, value) {
      this.answerDrafts = { ...this.answerDrafts, [questionId]: value }
      this.ensureQuestionTimer(questionId)
    },
    ensureQuestionTimer(questionId) {
      if (!questionId) return
      if (this.questionStartTimes[questionId]) return
      this.questionStartTimes = { ...this.questionStartTimes, [questionId]: Date.now() }
    },
    initTimersForLesson() {
      const lesson = this.currentLesson
      if (!lesson || !Array.isArray(lesson.practice)) return
      const now = Date.now()
      const next = { ...this.questionStartTimes }
      lesson.practice.forEach(question => {
        if (this.getQuestionAttempt(question.questionId)) return
        if (!next[question.questionId]) next[question.questionId] = now
      })
      this.questionStartTimes = next
    },
    consumeQuestionTimer(questionId) {
      const startedAt = this.questionStartTimes[questionId]
      if (!startedAt) return 0
      return Math.max(0, Date.now() - startedAt)
    },
    async submitMcqAnswer(question, optionIndex) {
      if (!this.currentLesson) return
      this.ensureQuestionTimer(question.questionId)
      await this.submitLessonAnswer(question, { answerIndex: optionIndex })
    },
    async submitShortAnswer(question) {
      if (!this.currentLesson) return
      const draft = this.answerDrafts[question.questionId] || ''
      if (!String(draft).trim()) return
      this.ensureQuestionTimer(question.questionId)
      await this.submitLessonAnswer(question, { answerText: draft })
    },
    async submitLessonAnswer(question, { answerIndex = null, answerText = '' } = {}) {
      const lesson = this.currentLesson
      if (!lesson || !this.selectedCourseId) return
      const timeMs = this.consumeQuestionTimer(question.questionId)
      this.answeringQuestion = { ...this.answeringQuestion, [question.questionId]: true }
      if (this.retryingQuestion[question.questionId]) {
        const nextRetry = { ...this.retryingQuestion }
        delete nextRetry[question.questionId]
        this.retryingQuestion = nextRetry
      }
      try {
        const response = await axios.post(
          `${this.journeyApiBase}/lesson-plan/lessons/${lesson.lessonId}/answer`,
          {
            questionId: question.questionId,
            answerIndex: Number.isInteger(answerIndex) ? answerIndex : null,
            answerText,
            timeMs,
          },
        )
        const payload = response.data || {}
        this.answerFeedback = {
          ...this.answerFeedback,
          [question.questionId]: {
            correct: !!payload.correct,
            explanation: payload.explanation || '',
            correctIndex: typeof payload.correctIndex === 'number' ? payload.correctIndex : null,
            expectedAnswer: payload.expectedAnswer || '',
            nudge: payload.nudge || '',
            conceptTag: payload.conceptTag || '',
            conceptLabel: payload.conceptLabel || '',
            submittedIndex: Number.isInteger(answerIndex) ? answerIndex : null,
            submittedText: answerText || '',
          },
        }
        if (payload.lessonProgress && lesson.progress) {
          lesson.progress.answeredCount = payload.lessonProgress.answeredCount
          lesson.progress.correctCount = payload.lessonProgress.correctCount
          lesson.progress.lastScore = payload.lessonProgress.lastScore
          lesson.progress.attempts = payload.lessonProgress.attempts || lesson.progress.attempts
          if (Array.isArray(payload.lessonProgress.liveInterventions)) {
            lesson.progress.liveInterventions = payload.lessonProgress.liveInterventions
          }
        }
        if (payload.liveIntervention && payload.liveIntervention.message) {
          this.announceLiveIntervention(payload.liveIntervention)
        }
      } catch (error) {
        this.toast('Réponse', error.response?.data?.error || 'Erreur en envoyant la réponse.', 'danger', 'AlertCircleIcon')
      } finally {
        const next = { ...this.answeringQuestion }
        delete next[question.questionId]
        this.answeringQuestion = next
      }
    },
    async completeCurrentLesson() {
      const lesson = this.currentLesson
      if (!lesson || !this.selectedCourseId || this.completingLesson) return
      this.completingLesson = true
      const completedLessonId = lesson.lessonId
      try {
        const response = await axios.post(
          `${this.journeyApiBase}/lesson-plan/lessons/${lesson.lessonId}/complete`,
        )
        if (response.data && response.data.journey) {
          this.journeyPayload = {
            ...(this.journeyPayload || {}),
            journey: response.data.journey,
          }
        }
        this.answerFeedback = {}
        this.answerDrafts = {}
        this.retryingQuestion = {}
        this.wrongOptionHistory = {}
        this.pinnedLessonId = completedLessonId
        this.$nextTick(() => {
          const el = this.$el && this.$el.querySelector('.coach-insight')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        const score = lesson.progress && lesson.progress.lastScore ? lesson.progress.lastScore : 0
        this.toast(
          'Leçon terminée',
          score >= 70 ? `Bravo ! ${score}% de réussite. Nick a préparé un retour.` : `Score : ${score}%. Regarde le retour de Nick ci-dessous.`,
          'success',
          'CheckCircleIcon',
        )
        const orchestrationEvent = response.data && response.data.orchestrationEvent
        const adaptiveEvent = response.data && response.data.adaptiveEvent
        if (orchestrationEvent && orchestrationEvent.message) {
          this.announceOrchestrationEvent(orchestrationEvent)
        } else if (adaptiveEvent && adaptiveEvent.message) {
          this.announceAdaptiveDetour(adaptiveEvent)
        }
        // Le profil peut avoir été ajusté par le moteur (learningMode / tone / support)
        // après cette leçon. On rafraîchit silencieusement pour que la carte profil
        // reflète la nouvelle réalité sans recharger la page.
        this.loadLearnerProfile(this.selectedCourseId)
      } catch (error) {
        this.toast('Leçon', error.response?.data?.error || 'Impossible de terminer la leçon.', 'danger', 'AlertCircleIcon')
      } finally {
        this.completingLesson = false
      }
    },
    continueAfterInsight() {
      const next = this.nextLessonAfterPinned
      this.pinnedLessonId = ''
      this.answerFeedback = {}
      this.retryingQuestion = {}
      this.wrongOptionHistory = {}
      if (next) {
        this.$nextTick(() => {
          const el = this.$el && this.$el.querySelector('.fh-lesson-heading, .fh-lesson-view, .fh-main')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    askNickAboutLesson() {
      const lesson = this.currentLesson
      if (!lesson) return
      this.sendCoachPrompt(`Peux-tu m'expliquer autrement : ${lesson.title} ?`)
    },
    askNickAboutConcept(feedback) {
      if (!feedback) return
      const label = feedback.conceptLabel || ''
      if (!label) {
        this.askNickAboutLesson()
        return
      }
      this.sendCoachPrompt(`Nick, peux-tu m'expliquer « ${label} » autrement avec un petit exemple ?`)
    },
    askNickAboutQuestion(question, feedback) {
      if (feedback && feedback.conceptLabel) {
        this.askNickAboutConcept(feedback)
        return
      }
      const promptText = question && question.prompt ? String(question.prompt).replace(/\s+/g, ' ').trim() : ''
      const lessonTitle = this.currentLesson ? this.currentLesson.title || '' : ''
      if (promptText) {
        const snippet = promptText.length > 180 ? `${promptText.slice(0, 179)}…` : promptText
        this.sendCoachPrompt(`Nick, je me suis trompé sur cette question : « ${snippet} ». Peux-tu m'expliquer pas à pas comment y répondre ?`)
        return
      }
      if (lessonTitle) {
        this.sendCoachPrompt(`Nick, je viens de me tromper sur un exercice de « ${lessonTitle} ». Peux-tu m'aider à comprendre où j'ai buggé ?`)
        return
      }
      this.askNickAboutLesson()
    },
    askNickFromInsight() {
      const insight = this.currentLessonInsight
      if (!insight) return
      const prompt = insight.recommendedPrompt && insight.recommendedPrompt.trim()
      if (prompt) {
        this.sendCoachPrompt(prompt)
      } else {
        this.askNickAboutLesson()
      }
    },
    async refreshCoachInsight() {
      const lesson = this.currentLesson
      if (!lesson || !this.selectedCourseId || this.refreshingInsight) return
      this.refreshingInsight = true
      try {
        const response = await axios.post(
          `${this.journeyApiBase}/lesson-plan/lessons/${lesson.lessonId}/coach-insight`,
        )
        const insight = response.data && response.data.insight
        if (insight && this.currentLesson) {
          this.currentLesson.coachInsight = insight
          if (this.journeyPayload) {
            this.journeyPayload = { ...this.journeyPayload }
          }
        }
      } catch (error) {
        this.toast('Coach', error.response?.data?.error || 'Impossible de rafraîchir le retour d\'Nick.', 'danger', 'AlertCircleIcon')
      } finally {
        this.refreshingInsight = false
      }
    },
    toggleToolsMode() {
      this.toolsMode = !this.toolsMode
    },
    getFileUrl(url) {
      return url ? `${axios.defaults.baseURL}${url}` : ''
    },
    async runPrimaryAction() {
      if (!this.primaryAction || !this.activeStep) return
      this.primaryBusy = true
      try {
        if (this.primaryAction.id === 'edit-profile') {
          this.openProfileModal()
        } else if (this.primaryAction.id === 'open-material') {
          this.openMaterialStep()
        } else if (this.primaryAction.id === 'generate-notes') {
          await this.generateNotesFromJourney()
        } else if (this.primaryAction.id === 'generate-flashcards') {
          this.openGeneratorFlow('flashcards')
        } else if (this.primaryAction.id === 'generate-quiz') {
          this.openGeneratorFlow('quiz')
        } else if (this.primaryAction.id === 'generate-practice') {
          this.openGeneratorFlow('guided_exercise')
        } else if (this.primaryAction.id === 'open-artifact') {
          await this.openArtifactInBot()
        } else if (this.primaryAction.id === 'open-coach') {
          this.sendCoachPrompt(this.coachPrompts[0] || 'Que dois-je faire maintenant ?')
        }
      } finally {
        this.primaryBusy = false
      }
    },
    openProfileModal() {
      const learner = this.learnerProfile || {}
      const profile = this.profile || {}
      const goal = learner.goal || profile.goal || {}
      const prefs = profile.preferences || {}
      const allowedObjectiveTypes = ['mastery', 'exam', 'grade', 'project']
      const objectiveType = allowedObjectiveTypes.includes(goal.objectiveType)
        ? goal.objectiveType
        : 'mastery'
      this.profileDraft = {
        objectiveType,
        currentLevel: learner.currentLevel || profile.currentLevel || 'intermediate',
        weeklyMinutes: Number(goal.weeklyMinutes) || 120,
        preferredStyle: learner.preferredStyle || prefs.preferredStyle || 'balanced',
      }
      this.showProfileModal = true
    },
    closeProfileModal() {
      if (this.savingProfile) return
      this.showProfileModal = false
    },
    async saveProfileEdits() {
      if (!this.selectedCourseId) return
      this.savingProfile = true
      try {
        const payload = {
          currentLevel: this.profileDraft.currentLevel,
          preferences: {
            preferredStyle: this.profileDraft.preferredStyle,
          },
          goal: {
            objectiveType: this.profileDraft.objectiveType,
            weeklyMinutes: Math.max(30, Math.min(1200, Number(this.profileDraft.weeklyMinutes) || 120)),
          },
        }

        const response = await axios.post(`/api/journey/${this.selectedCourseId}/refresh`, payload)
        this.journeyPayload = response.data
        this.activeStepId = this.getPreferredStepId(this.journeySteps, 'profile-overview')
        await Promise.all([
          this.loadCourses(),
          this.loadLearnerProfile(this.selectedCourseId),
        ])
        this.showProfileModal = false
        this.toast('Profil mis à jour', 'Ton plan a été recalculé.', 'success', 'CheckIcon')
      } catch (error) {
        this.toast(
          'Journey',
          error.response?.data?.error || 'Impossible de mettre à jour le profil.',
          'danger',
          'AlertCircleIcon',
        )
      } finally {
        this.savingProfile = false
      }
    },
    async runSecondaryAction() {
      if (!this.secondaryAction || !this.activeStep) return

      if (this.secondaryAction.id === 'refresh') {
        await this.refreshCurrentJourney()
        return
      }

      const topic = this.activeStep.topicLabel || this.activeStep.title
      if (this.secondaryAction.id === 'explain') {
        this.sendCoachPrompt(`Explique-moi ${topic} avec un exemple simple.`)
      } else if (this.secondaryAction.id === 'prime') {
        this.sendCoachPrompt(`Prépare-moi pour ${topic}.`)
      } else if (this.secondaryAction.id === 'coach-help') {
        this.sendCoachPrompt(`Aide-moi à avancer sur ${topic}.`)
      }
    },
    openMaterialStep() {
      const meta = this.activeStep && this.activeStep.meta ? this.activeStep.meta : {}
      const target = meta.externalUrl || this.getFileUrl(meta.fileUrl)
      if (!target) {
        this.sendCoachPrompt(`Résume ${this.activeStep.topicLabel || this.activeStep.title}.`)
        return
      }
      window.open(target, '_blank', 'noopener')
    },
    openGeneratorFlow(format) {
      const flow = this.$refs.exerciseFlow
      if (!flow || typeof flow.open !== 'function') return
      flow.open({
        format,
        difficulty: (this.activeStep && this.activeStep.difficulty)
          || (this.profile && this.profile.suggestedDifficulty)
          || 'medium',
      })
    },
    async fetchCompiledText() {
      const response = await axios.get(`/api/rag/courses/${this.selectedCourseId}/compiled-text?limitChars=9000`)
      const text = (response.data?.text || '').trim()
      if (text.length < 120) {
        throw new Error('Pas assez de contenu indexé pour ce cours. Ajoute du matériel puis réessaie.')
      }
      return text.substring(0, 9000)
    },
    async removeCourseFromJourney() {
      if (this.removingCourse || !this.selectedCourseId) return
      this.removingCourse = true
      try {
        await axios.delete(`/api/journey/courses/${this.selectedCourseId}`)
        this.showRemoveCourseModal = false
        this.courses = this.courses.filter(c => this.getEntityId(c) !== this.selectedCourseId)
        this.selectedCourseId = this.courses.length > 0 ? this.getEntityId(this.courses[0]) : ''
        this.journeyPayload = null
        this.toast('Cours retiré', 'Le cours a été retiré de ta Journey.', 'success', 'CheckIcon')
        if (this.courses.length === 0) {
          this.$router.push({ name: 'dashboard-student-my-courses' })
        }
      } catch (error) {
        this.toast('Erreur', error.response?.data?.error || 'Impossible de retirer ce cours.', 'danger', 'AlertCircleIcon')
      } finally {
        this.removingCourse = false
      }
    },
    buildLessonMindmapText(lesson) {
      if (!lesson) return ''
      const parts = []
      const title = typeof this.lessonDisplayTitle === 'function'
        ? this.lessonDisplayTitle(lesson)
        : (lesson.title || '')
      if (title) parts.push(title)
      if (lesson.subtitle) parts.push(lesson.subtitle)
      if (lesson.narrative) parts.push(lesson.narrative)
      if (lesson.whyItMatters) parts.push(lesson.whyItMatters)
      if (Array.isArray(lesson.keyPoints) && lesson.keyPoints.length) {
        parts.push(lesson.keyPoints.filter(Boolean).map(point => `- ${point}`).join('\n'))
      }
      if (lesson.example && lesson.example.body) {
        parts.push(`${lesson.example.title || 'Exemple'}: ${lesson.example.body}`)
      }
      return parts.filter(Boolean).join('\n\n')
    },
    async generateLessonMindmap() {
      if (this.generatingMindmap) return
      const bot = this.$refs.chatbot
      const lesson = this.currentLesson
      if (!bot || !lesson) return

      const text = this.buildLessonMindmapText(lesson)
      if (text.trim().length < 40) {
        this.toast('Carte mentale', 'Pas assez de contenu dans ce chapitre pour générer une carte.', 'warning', 'AlertCircleIcon')
        return
      }

      this.generatingMindmap = true
      try {
        const baseTitle = typeof this.lessonDisplayTitle === 'function'
          ? this.lessonDisplayTitle(lesson)
          : (lesson.title || 'Chapitre')
        const response = await axios.post('/api/ai/mindmap/from-text', {
          text,
          title: `${baseTitle} - Carte mentale`,
        })
        const mindmapData = response.data?.data
        if (!mindmapData) throw new Error('Impossible de générer la carte mentale.')

        bot.openMindmapPopup(mindmapData)
        await this.$nextTick()
        await bot.saveCurrentArtifact('mindmap')
        this.toast('Carte mentale', 'Ta carte mentale est prête.', 'success', 'CheckIcon')
      } catch (error) {
        this.toast('Carte mentale', error.response?.data?.error || error.message || 'Impossible de générer la carte mentale.', 'danger', 'AlertCircleIcon')
      } finally {
        this.generatingMindmap = false
      }
    },
    async generateNotesFromJourney() {
      const bot = this.$refs.chatbot
      if (!bot) return

      try {
        const text = await this.fetchCompiledText()
        const title = `${this.activeCourse ? this.activeCourse.title : 'Cours'} - Notes`
        const response = await axios.post('/api/ai/notes/from-text', { text, title })
        const notesData = response.data?.data
        if (!notesData) throw new Error('Impossible de générer la fiche.')

        bot.openNotesPopup(notesData)
        await this.$nextTick()
        await bot.saveCurrentArtifact('notes')
        await this.refreshJourneyWithFocus('latest-notes')
        this.toast('Fiche prête', 'Une nouvelle fiche a été créée pour la journey.', 'success', 'CheckIcon')
      } catch (error) {
        this.toast('Journey', error.response?.data?.error || error.message || 'Impossible de générer la fiche.', 'danger', 'AlertCircleIcon')
      }
    },
    async refreshJourneyWithFocus(stepKey) {
      if (!this.selectedCourseId) return
      const response = await axios.post(`/api/journey/${this.selectedCourseId}/refresh`, {})
      this.journeyPayload = response.data
      this.activeStepId = this.getPreferredStepId(this.journeySteps, stepKey)
      await this.loadCourses()
      await this.loadActiveArtifactPreview()
    },
    async openArtifactInBot() {
      const bot = this.$refs.chatbot
      if (!bot || !this.activeStep) return

      const mongoIdKey = '_id'
      if (!this.artifactPreview
        || !this.artifactDocument
        || String(this.artifactDocument[mongoIdKey]) !== String(this.activeStep.artifactId)) {
        await this.loadActiveArtifactPreview()
      }

      if (!this.artifactPreview) return

      if (this.activeStep.artifactType === 'quiz' && bot.openQuizPopup) {
        bot.openQuizPopup(this.artifactPreview)
      } else if (this.activeStep.artifactType === 'flashcards' && bot.openFlashcardsPopup) {
        bot.openFlashcardsPopup(this.artifactPreview)
      } else if (this.activeStep.artifactType === 'notes' && bot.openNotesPopup) {
        bot.openNotesPopup(this.artifactPreview)
      } else {
        bot.openChat()
      }
    },
    sendCoachPrompt(prompt) {
      const bot = this.$refs.chatbot
      if (!bot || !prompt) return
      if (typeof bot.openChat === 'function') bot.openChat()
      this.$nextTick(() => {
        if (typeof bot.sendQuickPrompt === 'function') {
          bot.sendQuickPrompt(prompt)
        }
      })
    },
    announceLiveIntervention(event) {
      const bot = this.$refs.chatbot
      if (!bot || !event || !event.message) return
      if (typeof bot.openChat === 'function') bot.openChat()
      this.$nextTick(() => {
        if (typeof bot.pushAssistantMessage === 'function') {
          bot.pushAssistantMessage(event.message, {
            kind: 'live-intervention',
            interventionKind: event.kind || '',
            conceptTag: event.conceptTag || '',
            conceptLabel: event.conceptLabel || '',
            questionId: event.questionId || '',
          })
        }
      })
    },
    announceAdaptiveDetour(event) {
      const bot = this.$refs.chatbot
      if (!bot || !event || !event.message) return
      if (typeof bot.openChat === 'function') bot.openChat()
      this.$nextTick(() => {
        if (typeof bot.pushAssistantMessage === 'function') {
          bot.pushAssistantMessage(event.message, {
            kind: 'adaptive-detour',
            adaptiveKind: event.kind || '',
            adaptiveLessonId: event.adaptiveLessonId || '',
            sourceLessonId: event.sourceLessonId || '',
            conceptTag: event.conceptTag || '',
            conceptLabel: event.conceptLabel || '',
          })
        }
      })
    },
    announceOrchestrationEvent(event) {
      const bot = this.$refs.chatbot
      if (!bot || !event || !event.message) return
      if (typeof bot.openChat === 'function') bot.openChat()
      this.$nextTick(() => {
        if (typeof bot.pushAssistantMessage === 'function') {
          bot.pushAssistantMessage(event.message, {
            kind: 'path-decision',
            decisionKind: event.kind || '',
            conceptTag: event.conceptTag || '',
            conceptLabel: event.conceptLabel || '',
            targetLessonId: event.targetLessonId || '',
            targetLessonTitle: event.targetLessonTitle || '',
          })
        }
      })
    },
    async handleExerciseQuiz(data) {
      const bot = this.$refs.chatbot
      if (!data || !bot || !bot.openQuizPopup) return
      bot.openQuizPopup(data)
      await this.$nextTick()
      if (typeof bot.saveCurrentArtifact === 'function') {
        await bot.saveCurrentArtifact('quiz')
      }
      await this.refreshJourneyWithFocus('latest-quiz')
    },
    async handleExerciseFlashcards(data) {
      const bot = this.$refs.chatbot
      if (!data || !bot || !bot.openFlashcardsPopup) return
      bot.openFlashcardsPopup(data)
      await this.$nextTick()
      if (typeof bot.saveCurrentArtifact === 'function') {
        await bot.saveCurrentArtifact('flashcards')
      }
      await this.refreshJourneyWithFocus('latest-flashcards')
    },
    async handleExerciseArtifactSaved() {
      await this.refreshJourneyWithFocus('latest-exercises')
    },
    async updateActiveStepStatus(status) {
      if (!this.selectedCourseId || !this.activeStep) return
      try {
        const response = await axios.patch(`/api/journey/${this.selectedCourseId}/steps/${this.activeStep.stepId}`, { status })
        this.journeyPayload = {
          ...(this.journeyPayload || {}),
          journey: response.data?.journey || null,
        }
        this.activeStepId = this.getPreferredStepId(this.journeySteps)
        await this.loadCourses()
        await this.loadActiveArtifactPreview()
      } catch (error) {
        this.toast('Journey', error.response?.data?.error || 'Impossible de mettre à jour l’étape.', 'danger', 'AlertCircleIcon')
      }
    },
    async completeActiveStep() {
      await this.updateActiveStepStatus('completed')
    },
    async reopenActiveStep() {
      await this.updateActiveStepStatus('available')
    },
  },
}
</script>

<style scoped>
.fh-page {
  min-height: 100vh;
  background: #f7f7fb;
  color: #1f2937;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.fh-state {
  max-width: 560px;
  margin: 56px auto;
  padding: 32px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  text-align: center;
}

.fh-state strong {
  display: block;
  margin-bottom: 8px;
  color: #0f172a;
  font-weight: 700;
}

.fh-state p {
  color: #64748b;
  margin: 0 0 16px;
  font-size: 0.92rem;
  line-height: 1.6;
}

.fh-state.inline {
  margin: 0;
  padding: 14px 16px;
  text-align: left;
  font-size: 0.9rem;
  color: #475569;
  background: #f8fafc;
}

.fh-spinner {
  width: 26px;
  height: 26px;
  margin: 0 auto 12px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  border-top-color: #6366f1;
  animation: fhSpin 0.8s linear infinite;
}

.fh-spinner.small {
  width: 18px;
  height: 18px;
  margin: 0;
  border-width: 2px;
}

@keyframes fhSpin {
  to { transform: rotate(360deg); }
}

.fh-shell {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr) 340px;
  gap: 0;
  min-height: 100vh;
}

.fh-sidebar,
.fh-main,
.fh-coach {
  min-width: 0;
}

.fh-sidebar {
  background: #fff;
  border-right: 1px solid #e5e7eb;
  padding: 18px 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  top: 0;
  align-self: stretch;
  height: 100vh;
  overflow-y: auto;
}

.fh-topbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fh-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 0;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  transition: color 0.15s ease;
}

.fh-back:hover {
  color: #4f46e5;
}

.fh-back svg {
  stroke: currentColor;
}

.fh-brand {
  font-size: 0.78rem;
  font-weight: 700;
  color: #4f46e5;
  letter-spacing: 0.02em;
}

.fh-course-select label {
  display: block;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 700;
}

.fh-course-select select {
  width: 100%;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  color: #0f172a;
  font-size: 0.88rem;
}

.fh-course-remove {
  margin-top: 6px;
}

.fh-course-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 2px;
  color: #94a3b8;
  font-size: 0.78rem;
  cursor: pointer;
  transition: color 0.2s;
}

.fh-course-remove-btn:hover:not(:disabled) {
  color: #ef4444;
}

.fh-course-remove-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fh-remove-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 20px 18px;
  text-align: center;
}

.fh-remove-modal-icon {
  font-size: 36px;
}

.fh-remove-modal h4 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.fh-remove-modal p {
  font-size: 0.88rem;
  color: #64748b;
  margin: 0;
  max-width: 300px;
}

.fh-remove-modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.fh-kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  color: #4f46e5;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 4px;
}

.fh-journey-head h2 {
  margin: 4px 0 6px;
  font-size: 1.02rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
}

.fh-journey-head p,
.fh-map-sub {
  margin: 0;
  color: #64748b;
  font-size: 0.83rem;
  line-height: 1.55;
}

.fh-map {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fh-steps {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.fh-step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 6px;
  border-radius: 6px;
  font-size: 0.87rem;
  color: #334155;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.fh-step:hover {
  background: #f1f5f9;
}

.fh-step.active {
  color: #4f46e5;
  font-weight: 700;
}

.fh-step-completed {
  color: #475569;
}

.fh-step-dot {
  font-size: 0.72rem;
  width: 16px;
  text-align: center;
  color: #94a3b8;
}

.fh-step-completed .fh-step-dot {
  color: #22c55e;
}

.fh-step-in_progress .fh-step-dot,
.fh-step.active .fh-step-dot {
  color: #4f46e5;
}

.fh-step-label {
  flex: 1;
  line-height: 1.4;
}

.fh-map-link {
  margin-top: 10px;
  background: none;
  border: none;
  color: #4f46e5;
  font-size: 0.84rem;
  text-align: left;
  padding: 6px 0;
  cursor: pointer;
}

.fh-map-link:hover {
  text-decoration: underline;
}

.fh-plan-side {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.fh-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-progress-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.fh-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  transition: width 0.3s ease;
}

.fh-progress-text {
  font-size: 0.78rem;
  color: #475569;
  font-weight: 600;
}

.fh-upgrade-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #c7d2fe;
  background: #eef2ff;
}

.fh-upgrade-card strong {
  color: #312e81;
  font-size: 0.84rem;
  font-weight: 800;
}

.fh-upgrade-card p {
  margin: 0;
  color: #4338ca;
  font-size: 0.78rem;
  line-height: 1.45;
}

.fh-lessons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fh-lesson-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}

.fh-lesson-item:hover {
  background: #f8fafc;
}

.fh-lesson-item.active {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.fh-lesson-item.adaptive {
  background: #faf5ff;
  border-color: #eadcff;
}

.fh-lesson-item.adaptive.active {
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.fh-lesson-item.fh-lesson-locked {
  opacity: 0.55;
  cursor: not-allowed;
}

.fh-lesson-item.fh-lesson-completed .fh-lesson-title {
  color: #0f172a;
}

.fh-lesson-item.adaptive .fh-lesson-title {
  color: #6b21a8;
}

.fh-lesson-num {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
}

.fh-lesson-item.active .fh-lesson-num {
  background: #4f46e5;
  color: #fff;
}

.fh-lesson-item.adaptive .fh-lesson-num {
  background: #ede9fe;
  color: #7c3aed;
}

.fh-lesson-item.fh-lesson-completed .fh-lesson-num {
  background: #10b981;
  color: #fff;
}

.fh-lesson-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fh-lesson-title {
  font-size: 0.88rem;
  color: #0f172a;
  font-weight: 600;
  line-height: 1.3;
}

.fh-lesson-kind {
  align-self: flex-start;
  background: #ede9fe;
  color: #7c3aed;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.fh-lesson-sub {
  font-size: 0.74rem;
  color: #64748b;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fh-lesson-dot {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 0.85rem;
  align-self: center;
}

.fh-tools-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.fh-lesson-view {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.fh-lesson-intro {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.fh-intro-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.fh-intro-card.big {
  max-width: 520px;
  padding: 32px;
  gap: 14px;
}

.fh-intro-card.outro {
  border-color: #c7d2fe;
  background: #eef2ff;
}

.fh-intro-tag {
  align-self: flex-start;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.fh-intro-card h1 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
  font-weight: 800;
  line-height: 1.3;
}

.fh-intro-card p {
  margin: 0;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.5;
}

.fh-intro-hint {
  color: #64748b !important;
  font-size: 0.82rem !important;
}

.fh-plan-error {
  color: #b91c1c !important;
  font-size: 0.88rem !important;
  background: #fee2e2;
  padding: 8px 12px;
  border-radius: 8px;
}
.fh-lock-hint {
  margin-top: 12px;
  color: #6b21a8 !important;
  font-size: 0.9rem !important;
  background: rgba(115, 103, 240, 0.1);
  padding: 10px 14px;
  border-radius: 10px;
  max-width: 520px;
}

.fh-lesson-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-lesson-kicker {
  font-size: 0.74rem;
  font-weight: 700;
  color: #4f46e5;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.fh-lesson-head h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
}

.fh-lesson-head p {
  margin: 0;
  color: #475569;
  font-size: 0.95rem;
}

.fh-lesson-block {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fh-lesson-block.narrative {
  background: #fafaff;
  border-color: #e0e7ff;
}

.fh-lesson-block.adaptive {
  background: #faf5ff;
  border-color: #e9d5ff;
}

.fh-lesson-block.adaptive.kind-challenge {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.fh-lesson-block.adaptive.kind-reexplain {
  background: #fff7ed;
  border-color: #fed7aa;
}

.fh-lesson-block.adaptive.kind-practice_boost {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.fh-lesson-block.adaptive.kind-spaced_review {
  background: #ecfeff;
  border-color: #a5f3fc;
}

.fh-lesson-block.narrative p {
  font-size: 1.02rem;
  line-height: 1.6;
  color: #1e293b;
  margin: 0;
}

.fh-lesson-block.why {
  background: #fffbeb;
  border-color: #fde68a;
}

.fh-lesson-block.transition {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.fh-block-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  color: #4f46e5;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.fh-lesson-block.why .fh-block-kicker {
  color: #b45309;
}

.fh-lesson-block.transition .fh-block-kicker {
  color: #0369a1;
}

.fh-lesson-block.adaptive .fh-block-kicker {
  color: #7c3aed;
}

.fh-lesson-block.adaptive.kind-challenge .fh-block-kicker {
  color: #15803d;
}

.fh-lesson-block.adaptive.kind-reexplain .fh-block-kicker {
  color: #c2410c;
}

.fh-lesson-block.adaptive.kind-practice_boost .fh-block-kicker {
  color: #1d4ed8;
}

.fh-lesson-block.adaptive.kind-spaced_review .fh-block-kicker {
  color: #0e7490;
}

.fh-lesson-block.adaptive.kind-practice_boost .fh-adaptive-pill {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.fh-lesson-block.adaptive.kind-spaced_review .fh-adaptive-pill {
  background: rgba(8, 145, 178, 0.12);
  color: #0e7490;
}

.fh-lesson-block.adaptive.kind-practice_boost .fh-adaptive-explain {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.fh-lesson-block.adaptive.kind-practice_boost .fh-adaptive-explain:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.fh-lesson-block.adaptive.kind-spaced_review .fh-adaptive-explain {
  background: #ecfeff;
  border-color: #a5f3fc;
  color: #155e75;
}

.fh-lesson-block.adaptive.kind-spaced_review .fh-adaptive-explain:hover {
  background: #cffafe;
  border-color: #67e8f9;
}

.fh-lesson-block p {
  margin: 0;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.55;
}

.fh-adaptive-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.fh-adaptive-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.12);
  color: #6d28d9;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.fh-lesson-block.adaptive.kind-challenge .fh-adaptive-pill {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.fh-lesson-block.adaptive.kind-reexplain .fh-adaptive-pill {
  background: rgba(234, 88, 12, 0.12);
  color: #c2410c;
}

.fh-adaptive-concept {
  font-size: 0.88rem;
  font-weight: 700;
  color: #334155;
}

.fh-adaptive-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.fh-adaptive-explain {
  appearance: none;
  border: 1px solid #c7d2fe;
  background: #eef2ff;
  color: #312e81;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 999px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.fh-adaptive-explain:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}

.fh-lesson-block.adaptive.kind-challenge .fh-adaptive-explain {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.fh-lesson-block.adaptive.kind-challenge .fh-adaptive-explain:hover {
  background: #ffedd5;
  border-color: #fdba74;
}

.fh-lesson-block.adaptive.kind-reexplain .fh-adaptive-explain {
  background: #ecfeff;
  border-color: #a5f3fc;
  color: #155e75;
}

.fh-lesson-block.adaptive.kind-reexplain .fh-adaptive-explain:hover {
  background: #cffafe;
  border-color: #67e8f9;
}

.fh-lesson-block.key-points ul {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-lesson-block.key-points li {
  color: #0f172a;
  font-size: 0.94rem;
  line-height: 1.5;
}

.fh-lesson-block.practice {
  gap: 14px;
}

.fh-lesson-block.outcomes {
  background: #f8fafc;
  border-color: #dbeafe;
}

.fh-lesson-block.formula {
  background: #f8fafc;
  border-color: #c7d2fe;
}

.fh-lesson-block.mindmap-block {
  background: #faf5ff;
  border-color: #e9d5ff;
  align-items: flex-start;
}

.fh-mindmap-hint {
  margin: 0 0 4px;
  color: #6b7280;
  font-size: 0.92rem;
}

.fh-outcomes-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fh-outcomes-list li {
  position: relative;
  padding-left: 18px;
  color: #0f172a;
  font-size: 0.92rem;
  line-height: 1.5;
}

.fh-outcomes-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  top: 0;
  color: #4f46e5;
  font-weight: 800;
}

.fh-formula-box {
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 14px 16px;
  overflow-x: auto;
}

.fh-formula-box :deep(.katex-display) {
  margin: 0;
}

.fh-formula-explanation {
  margin: 0;
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.5;
}

.fh-question {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fh-question.answered {
  background: #fff;
}

.fh-question-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.fh-question-num {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #4f46e5;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.fh-question-prompt {
  margin: 0;
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;
}

.fh-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  text-align: left;
  font-size: 0.9rem;
  color: #0f172a;
  transition: background 0.15s, border-color 0.15s;
}

.fh-option:hover:not(:disabled) {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.fh-option:disabled {
  cursor: default;
}

.fh-option.correct {
  background: #ecfdf5;
  border-color: #10b981;
  color: #064e3b;
}

.fh-option.wrong {
  background: #fef2f2;
  border-color: #ef4444;
  color: #7f1d1d;
}

.fh-option.muted {
  opacity: 0.55;
}

.fh-option-letter {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fh-option.correct .fh-option-letter {
  background: #10b981;
  color: #fff;
}

.fh-option.wrong .fh-option-letter {
  background: #ef4444;
  color: #fff;
}

.fh-short-answer {
  display: flex;
  gap: 8px;
}

.fh-short-answer input {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;
  background: #fff;
  color: #0f172a;
  outline: none;
}

.fh-short-answer input:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
}

.fh-btn.compact {
  padding: 8px 14px;
  font-size: 0.84rem;
}

.fh-feedback {
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.88rem;
}

.fh-feedback.correct {
  background: #ecfdf5;
  color: #064e3b;
  border: 1px solid #a7f3d0;
}

.fh-feedback.wrong {
  background: #fef2f2;
  color: #7f1d1d;
  border: 1px solid #fecaca;
}

.fh-feedback strong {
  font-size: 0.9rem;
}

.fh-feedback p {
  margin: 0;
  line-height: 1.45;
}

.fh-expected em {
  font-style: normal;
  font-weight: 700;
  color: inherit;
}

.fh-lesson-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.fh-lesson-block.coach-insight {
  background: linear-gradient(180deg, #f5f3ff 0%, #eef2ff 100%);
  border: 1px solid #c7d2fe;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fh-lesson-block.coach-insight.tone-positive {
  background: linear-gradient(180deg, #ecfdf5 0%, #f0fdfa 100%);
  border-color: #a7f3d0;
}

.fh-lesson-block.coach-insight.tone-careful {
  background: linear-gradient(180deg, #fefce8 0%, #fff7ed 100%);
  border-color: #fcd34d;
}

.fh-lesson-block.coach-insight.tone-alert {
  background: linear-gradient(180deg, #fef2f2 0%, #fff1f2 100%);
  border-color: #fca5a5;
}

.fh-coach-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fh-coach-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #4f46e5;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
}

.fh-lesson-block.coach-insight.tone-positive .fh-coach-avatar {
  background: #059669;
  box-shadow: 0 6px 16px rgba(5, 150, 105, 0.25);
}

.fh-lesson-block.coach-insight.tone-careful .fh-coach-avatar {
  background: #d97706;
  box-shadow: 0 6px 16px rgba(217, 119, 6, 0.25);
}

.fh-lesson-block.coach-insight.tone-alert .fh-coach-avatar {
  background: #dc2626;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.25);
}

.fh-coach-head-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.fh-coach-head-text strong {
  font-size: 0.98rem;
  color: #0f172a;
}

.fh-coach-sub {
  font-size: 0.78rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.fh-coach-refresh {
  margin-left: auto;
  border: 1px solid #cbd5f5;
  background: #fff;
  color: #4f46e5;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.2s;
}

.fh-coach-refresh:hover:not(:disabled) {
  background: #eef2ff;
}

.fh-coach-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fh-coach-remark,
.fh-coach-diagnosis,
.fh-coach-suggestion {
  margin: 0;
  font-size: 0.95rem;
  color: #1e293b;
  line-height: 1.55;
}

.fh-coach-diagnosis {
  color: #475569;
  font-size: 0.9rem;
}

.fh-coach-suggestion {
  font-weight: 600;
  color: #0f172a;
}

.fh-coach-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.fh-coach-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  border: 1px solid transparent;
  max-width: 100%;
}

.fh-coach-chip-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  opacity: 0.85;
}

.fh-coach-chip-body {
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
}

.fh-coach-chip.weak {
  background: #fff7ed;
  color: #b45309;
  border-color: #fdba74;
}

.fh-coach-chip.strong {
  background: #ecfdf5;
  color: #047857;
  border-color: #6ee7b7;
}

.fh-coach-continue {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed rgba(15, 23, 42, 0.1);
}

.fh-coach-continue-hint {
  color: #475569;
  font-size: 0.85rem;
  font-style: italic;
}

.fh-feedback-nudge {
  margin: 6px 0 2px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;
  line-height: 1.5;
}

.fh-feedback.correct .fh-feedback-nudge {
  color: #065f46;
}

.fh-feedback.wrong .fh-feedback-nudge {
  color: #991b1b;
}

.fh-feedback-actions {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.fh-coach-cta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.fh-coach-prompt-preview {
  font-size: 0.85rem;
  color: #475569;
  font-style: italic;
  flex: 1;
  min-width: 200px;
}

.fh-btn.ghost.compact {
  padding: 6px 14px;
  font-size: 0.85rem;
}

.fh-lesson-done {
  color: #047857;
  font-weight: 700;
  font-size: 0.9rem;
}

.fh-rich-body {
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.6;
}

.fh-lesson-block.narrative .fh-rich-body {
  font-size: 1.02rem;
  color: #1e293b;
}

.fh-rich-body strong,
.fh-lesson-head h1 strong {
  color: #0f172a;
  font-weight: 700;
}

.fh-rich-body em {
  font-style: italic;
}

.fh-inline-code {
  background: #f1f5f9;
  color: #0f172a;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: 'SFMono-Regular', 'Menlo', 'Monaco', monospace;
  font-size: 0.88em;
}

.fh-rich-body :deep(.fh-rich-paragraph) {
  margin: 0;
}

.fh-rich-body :deep(.fh-rich-paragraph + .fh-rich-paragraph) {
  margin-top: 10px;
}

.fh-rich-body :deep(.fh-rich-list) {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-rich-body :deep(.fh-rich-list + .fh-rich-paragraph),
.fh-rich-body :deep(.fh-rich-paragraph + .fh-rich-list),
.fh-rich-body :deep(.fh-rich-title + .fh-rich-paragraph),
.fh-rich-body :deep(.fh-rich-title + .fh-rich-list),
.fh-rich-body :deep(.fh-math-display + .fh-rich-paragraph),
.fh-rich-body :deep(.fh-math-display + .fh-rich-list) {
  margin-top: 10px;
}

.fh-rich-body :deep(.fh-rich-title) {
  margin: 0;
  color: #0f172a;
  font-weight: 800;
  line-height: 1.35;
}

.fh-rich-body :deep(.fh-rich-title.large) {
  font-size: 1.08rem;
}

.fh-rich-body :deep(.fh-rich-title.small) {
  font-size: 0.92rem;
}

.fh-rich-body :deep(.fh-math-display) {
  margin: 12px 0;
}

.fh-rich-body :deep(.katex) {
  font-size: 1em;
}

.fh-rich-body :deep(.katex-display) {
  margin: 10px 0;
}

.fh-option-text :deep(.katex),
.fh-question-prompt :deep(.katex) {
  font-size: 1em;
}

.fh-expected :deep(.katex) {
  font-size: 1em;
}

.fh-map-link:disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

.fh-main {
  background: #eef2ff;
  padding: 28px 32px 60px;
}

.fh-main-state {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.fh-step-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 760px;
  margin: 0 auto;
}

.fh-step-head {
  margin-bottom: 4px;
}

.fh-step-head h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  line-height: 1.35;
}

.fh-step-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 800;
  color: #ea580c;
  background: rgba(251, 146, 60, 0.12);
  padding: 4px 9px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fh-step-head p {
  margin: 6px 0 0;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.6;
}

.fh-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.fh-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #e5edff;
  border-bottom: 1px solid #dbeafe;
}

.fh-section-num {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #fff;
  color: #1d4ed8;
  font-weight: 800;
  font-size: 0.78rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fh-section-head h3 {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 800;
  color: #0f172a;
}

.fh-section-body {
  padding: 16px;
}

.fh-section-text p {
  margin: 0;
  color: #1f2937;
  font-size: 0.92rem;
  line-height: 1.65;
}

.fh-section-text p + p {
  margin-top: 8px;
}

.fh-section-list {
  margin: 0;
  padding-left: 18px;
  color: #1f2937;
  font-size: 0.92rem;
  line-height: 1.7;
}

.fh-cards,
.fh-notes,
.fh-quiz {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fh-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f8fafc;
}

.fh-card strong {
  display: block;
  color: #0f172a;
  font-size: 0.92rem;
  margin-bottom: 4px;
}

.fh-card span,
.fh-card p {
  display: block;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.55;
}

.fh-card ul {
  margin: 8px 0 0;
  padding-left: 18px;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.6;
}

.fh-prompts {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fh-prompt {
  width: 100%;
  text-align: left;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  color: #334155;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.15s ease, border 0.15s ease;
}

.fh-prompt:hover {
  border-color: #c7d2fe;
  background: #f5f3ff;
}

.fh-step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.fh-btn {
  border-radius: 7px;
  font-weight: 700;
  font-size: 0.86rem;
  padding: 9px 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border 0.15s ease, color 0.15s ease;
}

.fh-btn.primary {
  background: #4f46e5;
  color: #fff;
}

.fh-btn.primary:hover:not(:disabled) {
  background: #4338ca;
}

.fh-btn.ghost {
  background: #fff;
  border-color: #cbd5e1;
  color: #334155;
}

.fh-btn.ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.fh-btn.danger {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
}

.fh-btn.danger:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.fh-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.fh-coach {
  background: #fff;
  border-left: 1px solid #e5e7eb;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  align-self: stretch;
  height: 100vh;
  min-height: 100vh;
}

.fh-coach-head {
  padding: 18px 22px 12px;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
}

.fh-coach-title {
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.fh-coach-sub {
  font-size: 0.76rem;
  color: #94a3b8;
  margin-top: 2px;
}

.fh-coach-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.fh-coach-body ::v-deep .chatbot-container.embedded {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fh-coach-body ::v-deep .chatbot-container.embedded .chat-window {
  flex: 1 1 auto !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  border-radius: 0;
  border: none;
  box-shadow: none;
}

.fh-coach-body ::v-deep .chat-header {
  display: none !important;
}

.fh-coach-body ::v-deep .chat-messages {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  background: #fff;
  padding: 18px 18px 12px;
}

.fh-coach-body ::v-deep .welcome-section {
  text-align: left;
}

.fh-coach-body ::v-deep .welcome-card {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  text-align: left !important;
}

.fh-coach-body ::v-deep .welcome-card h3 {
  font-size: 0.95rem !important;
  color: #0f172a !important;
  text-align: left !important;
  margin-top: 0 !important;
}

.fh-coach-body ::v-deep .welcome-card p {
  font-size: 0.84rem !important;
  color: #475569 !important;
  text-align: left !important;
  line-height: 1.6 !important;
}

.fh-coach-body ::v-deep .welcome-icon-ring,
.fh-coach-body ::v-deep .welcome-icon-inner {
  display: none !important;
}

.fh-coach-body ::v-deep .message-visual-card {
  display: none !important;
}

.fh-coach-body ::v-deep .message-image-status {
  display: none !important;
}

.fh-coach-body ::v-deep .message-image-card {
  display: none !important;
}

.fh-coach-body ::v-deep .message-wrapper {
  margin-bottom: 14px;
}

.fh-coach-body ::v-deep .message-wrapper.user .message-bubble {
  background: #4f46e5 !important;
  color: #fff !important;
  border-radius: 12px 12px 4px 12px !important;
  box-shadow: none !important;
  padding: 10px 13px !important;
  font-size: 0.9rem !important;
}

.fh-coach-body ::v-deep .message-wrapper.assistant .message-bubble {
  background: #f1f5f9 !important;
  color: #0f172a !important;
  border-radius: 12px 12px 12px 4px !important;
  box-shadow: none !important;
  padding: 10px 13px !important;
  font-size: 0.9rem !important;
  border: none !important;
}

.fh-coach-body ::v-deep .message-content {
  line-height: 1.55 !important;
}

.fh-coach-body ::v-deep .message-time {
  font-size: 0.68rem !important;
  color: #94a3b8 !important;
  margin-top: 4px !important;
}

.fh-coach-body ::v-deep .chat-input-section {
  background: #fff;
  border-top: 1px solid #f1f5f9;
  padding: 12px 16px 14px;
}

.fh-coach-body ::v-deep .quick-prompts-row {
  display: none !important;
}

.fh-coach-body ::v-deep .welcome-section {
  display: none !important;
}

.fh-coach-body ::v-deep .input-container {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
}

.fh-coach-body ::v-deep .input-container textarea {
  font-size: 0.88rem !important;
}

.fh-coach-body ::v-deep .send-btn {
  background: #4f46e5 !important;
  color: #fff !important;
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
}

.fh-coach-body ::v-deep .send-btn:hover:not(:disabled) {
  background: #4338ca !important;
}

.fh-coach-body ::v-deep .typing-bubble {
  background: #f1f5f9 !important;
  border: none !important;
  box-shadow: none !important;
}

.fh-coach-body ::v-deep .sources-section {
  margin-top: 8px;
}

.fh-coach-body ::v-deep .sources-btn {
  font-size: 0.72rem !important;
  color: #64748b !important;
}

.fh-coach-body ::v-deep .input-cost-chip {
  font-size: 0.7rem !important;
}

@media (max-width: 1280px) {
  .fh-shell {
    grid-template-columns: 240px minmax(0, 1fr) 320px;
  }
}

@media (max-width: 1080px) {
  .fh-shell {
    grid-template-columns: 240px minmax(0, 1fr);
  }

  .fh-coach {
    display: none;
  }
}

@media (max-width: 720px) {
  .fh-shell {
    grid-template-columns: 1fr;
  }

  .fh-sidebar {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .fh-main {
    padding: 20px;
  }
}

.fh-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.fh-modal {
  background: #fff;
  border-radius: 14px;
  width: min(440px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}

.fh-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.fh-modal-head h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
}

.fh-modal-close {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #94a3b8;
  cursor: pointer;
  line-height: 1;
  padding: 0 6px;
}

.fh-modal-close:hover {
  color: #0f172a;
}

.fh-modal-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
}

.fh-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fh-field > span {
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.fh-field select,
.fh-field input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 11px;
  background: #fff;
  color: #0f172a;
  font-size: 0.9rem;
  outline: none;
}

.fh-field select:focus,
.fh-field input:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
}

.fh-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
}
</style>
